/**
 * Kismet Quiz V2 — state machine + UI + data egress
 *
 * Spec: PRD "Quiz / Loyalty / Profile / Subscription Management 2026" §2
 * Design: Figma "Flow Quiz V.2" (Kismet Design Explorations, node 3334-1066)
 *
 * Flow: start → per-dog loop (basics → lifestage → recipe) → email gate → results.
 * State: localStorage `kismet_quiz_v2` (versioned). The V2 Reveal
 * (quiz-v2-results.js) reads the same key — answers never travel in the URL.
 *
 * Data egress:
 *  - GA4/GTM dataLayer: new PRD taxonomy (quiz_started, quiz_step_completed,
 *    quiz_field_completed, quiz_name_fact_revealed, quiz_email_submitted,
 *    quiz_completed) with NO raw PII (names/emails hashed with ANALYTICS_SALT),
 *    plus the legacy `quizStep` event in parallel until GTM migrates.
 *  - Klaviyo onsite JS, hybrid model: per-step identify ONLY for already-known
 *    profiles (existing Klaviyo cookie); anonymous visitors are identified once,
 *    at the email gate. Legacy metric names ('Started Quiz'/'Completed Quiz') and
 *    dog_N_* props are kept so existing flows/emails don't break.
 *
 * Requires: quiz.calculator.js (shared with the live V1 quiz),
 * quiz-v2.validator.js, quiz.names.<letter>.js (dog name facts, loaded on demand).
 */

(function () {
 'use strict';

 // ── Constants ────────────────────────────────────────────────────────────
 var STORAGE_KEY = 'kismet_quiz_v2';
 var KLAVIYO_QUIZ_LIST_ID = 'QSm9FF';
 var QUIZ_VARIANT = 'v2';
 var CONSENT_VERSION = 'quiz-v2-2026-07';
 // Documented salt for hashing PII (dog name / email) before analytics.
 // If this ever changes, historical hashes stop matching — coordinate with data.
 var ANALYTICS_SALT = 'kismet-quiz-2026';
 var MAX_DOGS = 4;
 var DOG_SCREENS = ['basics', 'lifestage', 'recipe'];

 // P16 (reconciliation): allergy mixes that rule out BOTH recipes. Chicken
 // blocks Chicken & Barley; fish/salmon and also beef block Salmon & Brown
 // Rice (beef is in the nugs) → chicken+fish or chicken+beef leave nothing
 // safe to recommend, so the quiz ends on the Ask-a-Vet screen.
 function allergiesBlockAllRecipes(dog) {
  var a = (dog && dog.allergies) || [];
  if (a.indexOf('chicken') === -1) return false;
  return a.indexOf('fish') > -1 || a.indexOf('beef') > -1;
 }

 // PRD priority when several goals are selected: gut > hip&joint > senior >
 // skin&coat > energy > overall.
 var GOAL_PRIORITY = ['gut', 'hip_joint', 'senior_care', 'skin_coat', 'energy', 'overall'];
 var GOAL_LABELS = {
  gut: 'Fixing gut health',
  skin_coat: 'Skin and coat',
  energy: 'Energy and mood',
  hip_joint: 'Hip and joint',
  senior_care: 'Senior care',
  overall: 'Overall health'
 };
 var BREED_FALLBACKS = { poodle: 'poodle__standard', bulldog: 'bulldog__american' };

 // Start-card art (Shopify Files). `width=` asks the Shopify CDN for a resized
 // version — 2× the rendered size for retina. Keep in sync with the <link
 // rel="preload"> URLs in quiz-v2-main.liquid or the preload is wasted.
 var BOWL_IMG_URL = 'https://cdn.shopify.com/s/files/1/0817/9125/4828/files/bowl.png?v=1785257259&width=720';
 var AVATAR_PLACEHOLDER_URL = 'https://cdn.shopify.com/s/files/1/0817/9125/4828/files/placeholder-dog-avatars-bw.png?v=1786044589&width=400';

 // Photo upload: local preview is instant (canvas dataURL); the Worker then
 // stores the photo in R2 (hosted URL replaces the dataURL) and Gemini
 // preloads the breed for the basics screen. See uploadDogPhoto().
 // Controlled from the theme editor (Quiz V2 Main → "Enable photo upload");
 // only an explicit false turns it off, so contexts without the section var
 // keep the feature on.
 var ENABLE_PHOTO_UPLOAD = window.quizPhotoUploadEnabled !== false;

 // Quiz backend Worker (kismet-backend repo, quiz/). Empty string = disabled:
 // the vet question then only travels as a Klaviyo property, exactly the
 // pre-backend behavior, so this can ship to prod dark and be switched on once
 // the Worker is deployed. Local QA: 'http://127.0.0.1:8787' (wrangler dev).
 var QUIZ_BACKEND_URL = 'https://kismet-quiz-backend.ben-47b.workers.dev';
 // Local QA hook: point the quiz at a `wrangler dev` Worker without editing
 // the constant above (nothing to revert before a push). Console:
 //   localStorage.setItem('kismet_quiz_backend_url', 'http://127.0.0.1:8787')
 try {
  var backendOverride = localStorage.getItem('kismet_quiz_backend_url');
  if (backendOverride && /^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?$/.test(backendOverride)) {
   QUIZ_BACKEND_URL = backendOverride;
  }
 } catch (e) { /* noop */ }

 // ── Small helpers ────────────────────────────────────────────────────────
 // Klaviyo's onsite cookie is base64 JSON carrying `$exchange_id`, the id of
 // the profile this browser is bound to. The Worker uses it to reconcile the
 // pre-gate (draft) profile against the confirmed email — read it BEFORE the
 // gate identify, which may re-bind the cookie to the new address.
 function readKlaviyoExchangeId() {
  try {
   var m = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]*)/);
   if (!m) return '';
   var obj = JSON.parse(atob(decodeURIComponent(m[1])));
   return obj && obj.$exchange_id ? String(obj.$exchange_id) : '';
  } catch (e) { return ''; }
 }

 function getBreedSlug(breed) {
  if (!breed) return '';
  // Accents never survive a Shopify Files upload (Vendéen → vendeen), so
  // normalize here — the face URL already did; the side art now matches.
  var lower = breed.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  var parenMatch = lower.match(/^(.+?)\s*\((.+?)\)$/);
  if (parenMatch) {
   return parenMatch[1].trim().replace(/['’]/g, '_').replace(/\s+/g, '_') + '__' +
          parenMatch[2].trim().replace(/['’]/g, '_').replace(/\s+/g, '_');
  }
  var slug = lower.replace(/['’]/g, '_').replace(/\s+/g, '_');
  return BREED_FALLBACKS[slug] || slug;
 }
 window.getBreedSlug = getBreedSlug;

 function getBreedFaceUrl(breed, width) {
  if (!breed) return '';
  var base = window.SHOPIFY_CDN_BASE_URL || '';
  var slug = getBreedSlug(breed).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return base + 'breed_' + slug + '_face.png?width=' + (width || 200);
 }

 // Full-body illustration. Female dogs get the `_female` art (same drawing +
 // bow, same canvas — client delivery 2026-09-08, 215/215 on the CDN). The
 // face avatars have no female version yet (decision 2026-09-09), so
 // getBreedFaceUrl stays sex-agnostic. `sex` is optional: '' / 'male' → male art.
 // FEMALE_ART_V: cache-buster for the female set. The CDN caches the bare
 // path for a year, so a corrected file re-uploaded under the same name keeps
 // serving the old pixels — bump this when the illustrator replaces files
 // (v2: Cavalier / Great Dane / Labrador corrections, 2026-09-24).
 var FEMALE_ART_V = 2;
 function getBreedArtUrl(breed, sex, width) {
  if (!breed) return '';
  var base = window.SHOPIFY_CDN_BASE_URL || '';
  var female = sex === 'female';
  var params = [];
  if (female) params.push('v=' + FEMALE_ART_V);
  if (width) params.push('width=' + width);
  return base + 'breed_' + getBreedSlug(breed) + (female ? '_female' : '') + '.png' + (params.length ? '?' + params.join('&') : '');
 }

 // Requested by Kismet: the dog's name always renders with a capital first
 // letter (quiz screens, results page, Klaviyo props). Only the first character
 // is touched so names like "JoJo" or "McFly" keep the owner's casing.
 function normalizeDogName(name) {
  var n = (name || '').trim().slice(0, 21);
  return n ? n.charAt(0).toUpperCase() + n.slice(1) : n;
 }

 // Name fun facts ship as one file per first letter (quiz.names.a.js …,
 // built by scripts/build-quiz-name-facts.mjs; URLs in window.quizNameFactsSrc),
 // and only the file for the letter being typed is fetched. Case-insensitive
 // exact match; iOS smart punctuation types ’ (or ‘ at the start of a word)
 // for ', so both are folded before matching.
 var nameFactIndex = Object.create(null); // a dog named "constructor" must not hit Object.prototype
 var nameFactGroupsRequested = {};
 function nameFactKey(name) {
  return (name || '').trim().toLowerCase().replace(/[\u2018\u2019]/g, "'");
 }
 // Same rule as the build script: first letter or digit once accents are
 // stripped; digits and symbols ("2Pac") live in the "a" file.
 function nameFactGroup(key) {
  var c = key.normalize('NFD').replace(/[^a-z0-9]/g, '').charAt(0);
  return c >= 'a' && c <= 'z' ? c : 'a';
 }

 function buildDogsDisplayName(names) {
  var list = names.filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return list[0] + ' & ' + list[1];
  return list.slice(0, -1).join(', ') + ' & ' + list[list.length - 1];
 }

 function deviceType() {
  return window.matchMedia && window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
 }

 function makeQuizId() {
  return 'q_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
 }

 // sha256(salt + value) → hex. Used so analytics never carries raw PII.
 function hashForAnalytics(value) {
  if (!value || !window.crypto || !window.crypto.subtle) return Promise.resolve('');
  var data = new TextEncoder().encode(ANALYTICS_SALT + '|' + String(value).trim().toLowerCase());
  return window.crypto.subtle.digest('SHA-256', data).then(function (buf) {
   return Array.prototype.map.call(new Uint8Array(buf), function (b) {
    return ('0' + b.toString(16)).slice(-2);
   }).join('');
  }).catch(function () { return ''; });
 }

 function getUTMParams() {
  var utm = {};
  try {
   var params = new URLSearchParams(window.location.search);
   ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
    if (params.get(k)) utm[k] = params.get(k);
   });
  } catch (e) { /* noop */ }
  return utm;
 }

 function hasRealAllergies(dog) {
  return (dog.allergies || []).filter(function (a) { return a !== 'none'; }).length > 0;
 }

 function topHealthGoal(goals) {
  for (var i = 0; i < GOAL_PRIORITY.length; i++) {
   if ((goals || []).indexOf(GOAL_PRIORITY[i]) > -1) return GOAL_PRIORITY[i];
  }
  return '';
 }

 var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

 // Age in months from the (required) full birthday; null while incomplete.
 function dogAgeMonths(dog) {
  if (window.QuizResolver) return window.QuizResolver.ageMonths(dog);
  var year = parseInt(dog.birthdayYear, 10);
  var month = MONTHS.indexOf(dog.birthdayMonth);
  var day = parseInt(dog.birthdayDay, 10);
  if (!year || month < 0 || !day) return null;
  var now = new Date();
  var months = (now.getFullYear() - year) * 12 + (now.getMonth() - month);
  if (now.getDate() < day) months -= 1;
  return Math.max(0, months);
 }

 function isSeniorDog(dog) {
  var months = dogAgeMonths(dog);
  return months !== null && months >= 96; // 8+ years → senior route (PRD)
 }

 // "4 years old" / "7 months old" for the recap line.
 function ageLabel(dog) {
  var months = dogAgeMonths(dog);
  if (months === null) return '';
  if (months < 12) return months + (months === 1 ? ' month old' : ' months old');
  var years = Math.floor(months / 12);
  return years + (years === 1 ? ' year old' : ' years old');
 }

 /**
  * Avatar routing per PRD §2 (4b): declared motivation → hard life-stage →
  * current food (fresh → Fiona) → health goal → fallback.
  * Health-goal→avatar map beyond gut/senior is provisional (flagged in the plan).
  */
 function resolveAvatar(dog) {
  var byMotivation = {
   gut: 'grace', price: 'fiona', senior: 'susan',
   kibble_guilt: 'katie', labels: 'olivia', new_puppy: 'paige'
  };
  if (byMotivation[dog.motivation]) return byMotivation[dog.motivation];
  var months = dogAgeMonths(dog);
  if (months !== null && months < 12) return 'paige';
  if (isSeniorDog(dog)) return 'susan';
  if (dog.currentFoodType === 'fresh') return 'fiona';
  var goal = topHealthGoal(dog.healthGoals);
  if (goal === 'gut') return 'grace';
  if (goal === 'senior_care' || goal === 'hip_joint') return 'susan';
  if (goal === 'skin_coat' || goal === 'energy') return 'olivia';
  return 'grace'; // fallback — pending confirmation (PRD leaves it open)
 }

 function emptyDog() {
  return {
   name: '',
   libraryMatch: false,
   nameFactShown: false,
   photo: '',
   breed: '',
   detectedBreed: '',
   sex: '',
   weight: '',
   bodyCondition: '',
   spayNeuter: '',
   birthdayMonth: '',
   birthdayDay: '',
   birthdayYear: '',
   activity: '',
   motivation: '',
   healthGoals: [],
   allergies: [],
   currentFoodType: '',
   currentFoodBrand: '',
   screensCompleted: 0
  };
 }

 function defaultState() {
  return {
   version: 2,
   quizId: makeQuizId(),
   screen: 'start',
   dogIndex: 0,
   email: '',
   // Where a filled-in email came from ('klaviyo_form' / 'quiz' / 'account')
   // and its value, so completion can report whether the shopper changed it
   emailSource: '',
   emailFormId: '',
   emailPrefilled: '',
   // Set by every draft identify (the last non-empty cookie id wins): which
   // Klaviyo cookie the drafts went to and that one ran at all. Persisted so
   // the gate can report it even after a reload where Klaviyo no longer
   // recognizes the browser (a cookie profile was left "abandoned" that way,
   // 2026-10-26).
   klaviyoDraftKx: '',
   klaviyoDraftWritten: false,
   vetQuestion: '',
   startedAt: null,
   completedAt: null,
   utm: getUTMParams(),
   dogs: [emptyDog()]
  };
 }

 // ── Application ──────────────────────────────────────────────────────────
 var KismetQuiz = {
  state: defaultState(),
  errors: [],
  zodiacSigns: [],
  klaviyoKnown: false,

  init: function () {
   this.zodiacSigns = (window.quizMetaobjects && window.quizMetaobjects.zodiacSigns) || [];
   this.load();
   this.prefillEmail();
   // Already finished the quiz → straight to the Reveal (which reads the same
   // stored answers). "Create plan for another dog" there clears the state,
   // so a shopper who wants to redo the quiz still has a way back in.
   if (this.state.completedAt) {
    this.redirectToResults();
    return;
   }
   this.detectKnownKlaviyoProfile();
   this.bindEvents();
   this.render();
   // Lift the load veil only after the correct screen is fully rendered
   var wrapper = document.getElementById('quiz-wrapper');
   if (wrapper) wrapper.classList.add('is-ready');
  },

  // Fetches a name facts file the first time a name in its letter range is
  // looked up. Names typed meanwhile get their fact as soon as it lands.
  loadNameFactGroup: function (group) {
   var self = this;
   var src = group && window.quizNameFactsSrc && window.quizNameFactsSrc[group];
   if (!src || nameFactGroupsRequested[group]) return;
   nameFactGroupsRequested[group] = true;
   var script = document.createElement('script');
   script.src = src;
   script.async = true;
   script.onload = function () {
    var facts = (window.quizNameFacts && window.quizNameFacts[group]) || {};
    Object.keys(facts).forEach(function (n) { nameFactIndex[nameFactKey(n)] = facts[n]; });
    self.state.dogs.forEach(function (dog, i) { self.updateNameFact(i); });
   };
   // A failed fetch is retried on the next keystroke
   script.onerror = function () { nameFactGroupsRequested[group] = false; };
   document.head.appendChild(script);
  },

  // ── Persistence ─────────────────────────────────────────────────────────
  load: function () {
   try {
    // WIP note: while V2 lives alongside the production quiz we never touch the
    // v1 localStorage keys (a QA visit must not wipe a shopper's live-quiz
    // progress). V2 state lives entirely under its own key.
    var raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    var saved = JSON.parse(raw);
    if (!saved || saved.version !== 2 || !Array.isArray(saved.dogs) || saved.dogs.length === 0) return;
    // Re-hydrate over defaults so new fields added later get sane values.
    var base = defaultState();
    this.state = Object.assign(base, saved);
    this.state.dogs = saved.dogs.map(function (d) {
     var dog = Object.assign(emptyDog(), d);
     dog.name = normalizeDogName(dog.name); // sessions saved before the capitalization rule
     return dog;
    });
    if (this.state.dogIndex >= this.state.dogs.length) this.state.dogIndex = 0;
    if (this.state.screen === 'loading') this.state.screen = 'gate';
   } catch (e) {
    console.error('[Kismet Quiz] load error:', e);
    this.state = defaultState();
   }
  },

  save: function () {
   // Single path to localStorage, so normalizing here covers every write site
   // (start inputs, inline rename) plus everything downstream that reads it.
   this.state.dogs.forEach(function (dog) { dog.name = normalizeDogName(dog.name); });
   try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
   } catch (e) { /* private mode etc. */ }
  },

  // Fills the email fields (gate / vet-end) (KISDEV-461). A typed or edited
  // address always wins; an untouched filled-in one is recomputed on every
  // load, so a newer source replaces it. The logged-in customer's email comes
  // first (on a shared device the stored one may belong to someone else),
  // then the one the visitor last gave us on this device — a Klaviyo form or
  // an earlier quiz, kept by snippets/utility.known-email.liquid.
  // The field stays editable and the gate's consent copy is unchanged.
  prefillEmail: function () {
   if (this.state.email && this.state.email !== this.state.emailPrefilled) return;
   var known = window.KismetKnownEmail && window.KismetKnownEmail.get();
   if (window.quizCustomerEmail) {
    this.state.email = window.quizCustomerEmail;
    this.state.emailSource = 'account';
    this.state.emailFormId = '';
   } else if (known) {
    this.state.email = known.email;
    this.state.emailSource = known.source;
    this.state.emailFormId = known.formId || '';
   } else {
    return;
   }
   this.state.emailPrefilled = this.state.email;
  },

  // Gate / vet-end submit: remember the confirmed address for the next quiz
  // on this device ("Create plan for another dog" wipes the quiz state).
  rememberEmail: function () {
   if (window.KismetKnownEmail) window.KismetKnownEmail.set(this.state.email, 'quiz');
  },

  // `quiz_email_source` / `quiz_email_changed` on Completed Quiz: 'typed' when
  // nothing was filled in; changed = the shopper edited the filled-in address.
  emailSourceProps: function () {
   var prefilled = this.state.emailPrefilled;
   var props = {
    quiz_email_source: prefilled ? this.state.emailSource : 'typed',
    quiz_email_changed: !!prefilled && prefilled.toLowerCase() !== (this.state.email || '').toLowerCase()
   };
   if (prefilled && this.state.emailFormId) props.quiz_email_form_id = this.state.emailFormId;
   return props;
  },

  // ── Klaviyo known-profile detection (hybrid identify model) ─────────────
  detectKnownKlaviyoProfile: function () {
   var self = this;
   var hasCookie = document.cookie.indexOf('__kla_id=') > -1;
   if (!hasCookie) return;
   // isIdentified() exists on the new onsite object; treat cookie-only as known
   // enough for property identifies (no email is ever sent before the gate).
   if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
    try {
     window.klaviyo.isIdentified().then(function (known) { self.klaviyoKnown = !!known; });
    } catch (e) { self.klaviyoKnown = true; }
   } else {
    self.klaviyoKnown = true;
   }
  },

  // ── Events ──────────────────────────────────────────────────────────────
  bindEvents: function () {
   var self = this;
   var wrapper = document.getElementById('quiz-wrapper');
   if (!wrapper) return;

   // Pills carry measured px widths — re-measure when a resize crosses a
   // breakpoint and the media-query font sizes change (labels were clipping)
   var resizeTimer = null;
   window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
     self.sizeSelects();
     self.sizeBreedInput();
     self.sizeWeightInput();
     self.sizeSumWeight();
    }, 120);
   });

   // Delegated clicks
   wrapper.addEventListener('click', function (e) {
    var t = e.target;

    var multiOpt = t.closest('.quiz2-multi-option');
    if (multiOpt) { self.toggleMultiOption(multiOpt); return; }
    if (t.closest('[data-multi-close]')) { self.closeMultiPanels(); return; }
    var multiTrigger = t.closest('.quiz2-multi-trigger');
    if (multiTrigger) { self.toggleMultiPanel(multiTrigger.closest('[data-multi]')); return; }

    if (t.closest('#quiz2-start-cta')) { self.handleStartContinue(); return; }
    if (t.closest('[data-continue]')) { self.handleContinue(); return; }
    if (t.closest('#quiz2-gate-cta')) { self.handleGateSubmit(); return; }
    if (t.closest('#quiz2-vet-end-cta')) { self.handleVetEndSubmit(); return; }
    var suggestBtn = t.closest('[data-email-suggest]');
    if (suggestBtn) { self.handleEmailSuggestion(suggestBtn); return; }
    // Fresh quiz for the next dog (multi-dog is serial while deferred):
    // wipe the stored state and reload straight into the start screen
    if (t.closest('#quiz2-vet-end-restart')) {
     try { localStorage.removeItem(STORAGE_KEY); } catch (err) { /* noop */ }
     window.location.reload();
     return;
    }
    if (t.closest('#quiz-close-btn')) { self.handleClose(); return; }

    if (t.closest('#quiz2-add-dog')) { self.handleAddDog(); return; }
    var removeBtn = t.closest('[data-remove-dog]');
    if (removeBtn) { self.handleRemoveDog(parseInt(removeBtn.dataset.removeDog, 10)); return; }

    var uploadBtn = t.closest('[data-upload-photo]');
    if (uploadBtn) {
     self._photoTarget = parseInt(uploadBtn.dataset.uploadPhoto, 10);
     self._photoInput.click();
     return;
    }

    if (t.closest('#quiz2-same-as')) { self.handleSameAsDog1(); return; }

    // Click the dog's name in a sentence to rename it inline (no back button).
    // Not in the Ask-a-Vet card — the name there is display-only (QA 2026-08-06).
    var nameSpan = t.closest('[data-dog-name]');
    if (nameSpan && !nameSpan.closest('.quiz2-vet-end') && DOG_SCREENS.indexOf(self.state.screen) > -1) { self.editDogName(nameSpan); return; }

    var switchBtn = t.closest('[data-switch-dog]');
    if (switchBtn) { self.handleSwitchDog(parseInt(switchBtn.dataset.switchDog, 10)); return; }

   });

   // ── DEV SHORTCUT (temporary, remove before launch) ────────────────────
   // Ctrl+Shift+1…9 jumps straight to that step, skipping validation:
   // 1 = start, then the per-dog screens in order, last = email gate.
   document.addEventListener('keydown', function (e) {
    if (!e.ctrlKey || !e.shiftKey || e.metaKey) return;
    var m = /^Digit([1-9])$/.exec(e.code);
    if (!m) return;
    var seq = self.screenSequence();
    var target = seq[parseInt(m[1], 10) - 1];
    if (!target) return;
    e.preventDefault();
    self.state.screen = target.screen;
    self.state.dogIndex = target.dogIndex;
    self.errors = [];
    self.save();
    self.render();
   });

   // Delegated field changes (breed input is owned by the typeahead, not this)
   wrapper.addEventListener('change', function (e) {
    var el = e.target;
    if (!el.dataset || !el.dataset.field || el.id === 'quiz2-breed') return;
    self.handleFieldInput(el, true);
   });
   wrapper.addEventListener('input', function (e) {
    var el = e.target;
    if (!el.dataset || !el.dataset.field || el.id === 'quiz2-breed') return;
    self.handleFieldInput(el, false);
   });

   // Enter advances on gate email / start names
   wrapper.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var el = document.activeElement;
    if (!el) return;
    if (el.id === 'quiz2-email') { e.preventDefault(); self.handleGateSubmit(); }
    if (el.id === 'quiz2-vet-end-email') { e.preventDefault(); self.handleVetEndSubmit(); }
    if (el.classList && el.classList.contains('quiz2-dog-name-input')) { e.preventDefault(); self.handleStartContinue(); }
   });

   // Hidden file input for the dog photo (shared across cards)
   var photoInput = document.createElement('input');
   photoInput.type = 'file';
   photoInput.accept = 'image/*';
   photoInput.style.display = 'none';
   document.body.appendChild(photoInput);
   photoInput.addEventListener('change', function () {
    if (this.files && this.files[0]) self.handlePhotoPicked(this.files[0]);
    this.value = '';
   });
   this._photoInput = photoInput;

   // Multi-select panels close on outside click / Escape (Close row + screen
   // changes are handled elsewhere)
   document.addEventListener('mousedown', function (e) {
    if (!(e.target instanceof Element) || !e.target.closest('.quiz2-multi')) self.closeMultiPanels();
   });
   document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') self.closeMultiPanels();
   });

   this.initBreedTypeahead();
   this.initCustomDropdowns();
  },

  // Custom dropdown panel for every <select> (native popups can't be styled).
  // Delegated: covers re-rendered recap chips too. The Figma shows the same
  // white panel on mobile, so touch uses it as well (native picker suppressed).
  initCustomDropdowns: function () {
   var self = this;
   var wrapper = document.getElementById('quiz-wrapper');
   if (!wrapper) return;
   var panel = document.createElement('div');
   panel.className = 'quiz2-dd-panel';
   panel.setAttribute('hidden', '');
   wrapper.appendChild(panel);
   this._ddPanel = panel;
   this._ddSelect = null;
   var activeIdx = -1;

   function close() {
    panel.setAttribute('hidden', '');
    self._ddSelect = null;
    activeIdx = -1;
   }
   this._ddClose = close;

   function open(sel) {
    var opts = Array.prototype.filter.call(sel.options, function (o) { return o.value !== ''; });
    if (!opts.length) return;
    panel.innerHTML = opts.map(function (o) {
     return '<div class="quiz2-dd-option' + (o.value === sel.value ? ' is-selected' : '') +
      '" data-value="' + o.value.replace(/"/g, '&quot;') + '">' + o.textContent + '</div>';
    }).join('');
    panel.removeAttribute('hidden');
    var r = sel.getBoundingClientRect();
    panel.style.minWidth = Math.round(r.width) + 'px';
    panel.style.left = Math.round(r.left) + 'px';
    panel.style.top = Math.round(r.bottom + 6) + 'px';
    var ph = panel.getBoundingClientRect().height;
    if (r.bottom + 6 + ph > window.innerHeight - 8) {
     panel.style.top = Math.max(8, Math.round(r.top - ph - 6)) + 'px'; // flip above
    }
    activeIdx = -1;
    self._ddSelect = sel;
   }

   wrapper.addEventListener('mousedown', function (e) {
    var sel = e.target.closest('select');
    if (sel) {
     e.preventDefault(); // suppress the native popup
     if (self._ddSelect === sel) { close(); return; }
     sel.focus();
     open(sel);
     return;
    }
    if (!e.target.closest('.quiz2-dd-panel')) close();
   });

   // iOS launches the native picker from TOUCHEND (before any mouse event),
   // so the mousedown/click preventDefaults alone showed BOTH pickers on
   // iPhone. Intercept the touch itself: preventDefault here cancels the
   // focus + native picker + synthetic mouse events, and the panel toggles
   // from this handler instead. No sel.focus() — focusing a select is what
   // re-triggers the native wheel on iOS.
   wrapper.addEventListener('touchend', function (e) {
    var sel = e.target.closest('select');
    if (!sel) return;
    e.preventDefault();
    if (self._ddSelect === sel) { close(); return; }
    open(sel);
   }, { passive: false });

   // Safety net for browsers that still synthesize a click on the select
   wrapper.addEventListener('click', function (e) {
    if (e.target.closest('select')) e.preventDefault();
   });

   panel.addEventListener('mousedown', function (e) { e.preventDefault(); });
   panel.addEventListener('click', function (e) {
    var optEl = e.target.closest('.quiz2-dd-option');
    if (!optEl || !self._ddSelect) return;
    self._ddSelect.value = optEl.dataset.value;
    self._ddSelect.dispatchEvent(new Event('change', { bubbles: true }));
    close();
   });

   document.addEventListener('keydown', function (e) {
    if (self._ddSelect) {
     var options = panel.querySelectorAll('.quiz2-dd-option');
     if (e.key === 'Escape' || e.key === 'Tab') {
      close();
     } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      activeIdx = e.key === 'ArrowDown'
       ? Math.min(activeIdx + 1, options.length - 1)
       : Math.max(activeIdx - 1, 0);
      options.forEach(function (o, i) { o.classList.toggle('is-active', i === activeIdx); });
      if (options[activeIdx]) options[activeIdx].scrollIntoView({ block: 'nearest' });
     } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (activeIdx > -1 && options[activeIdx]) { options[activeIdx].click(); } else { close(); }
     }
     return;
    }
    var focused = document.activeElement;
    if (focused && focused.tagName === 'SELECT' &&
        (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
     e.preventDefault();
     open(focused);
    }
   });

   // Close when the PAGE scrolls (the anchor pill moves away) — but not when
   // the user scrolls inside the panel itself (long lists: months, years).
   window.addEventListener('scroll', function (e) {
    if (e.target instanceof Element && panel.contains(e.target)) return;
    close();
   }, true);
   window.addEventListener('resize', close);
  },

  // Browser-local photo preview: compress via canvas so it fits localStorage,
  // then hand the same compressed frame to the Worker (R2 + Gemini breed ID).
  handlePhotoPicked: function (file) {
   var self = this;
   var idx = this._photoTarget;
   var dog = this.state.dogs[idx];
   if (!dog || !file.type || file.type.indexOf('image/') !== 0) return;

   var objectUrl = URL.createObjectURL(file);
   var img = new Image();
   img.onload = function () {
    var MAX = 512;
    var scale = Math.min(1, MAX / Math.max(img.width, img.height));
    var canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(objectUrl);
    dog.photo = canvas.toDataURL('image/jpeg', 0.82);
    try {
     self.save();
    } catch (e) {
     // localStorage quota: keep the photo in memory only for this session
     console.warn('[Kismet Quiz] photo not persisted (storage quota)');
    }
    self.updateStartAvatar(idx);
    self.renderDogNames();
    self.renderProgress();
    canvas.toBlob(function (blob) { self.uploadDogPhoto(idx, blob); }, 'image/jpeg', 0.82);
   };
   img.onerror = function () { URL.revokeObjectURL(objectUrl); };
   img.src = objectUrl;
  },

  // Swap only the avatar circle (and the button label) of one start card.
  // A full renderStart() would recreate every <img> — including the bowl,
  // which blinks for a frame while it re-decodes.
  updateStartAvatar: function (idx) {
   var container = document.getElementById('quiz2-dog-cards');
   var card = container && container.children[idx];
   if (!card) { this.renderStart(); return; }
   var circle = card.querySelector('.quiz2-dog-circle');
   if (circle) {
    circle.innerHTML = this.dogAvatarHtml(this.state.dogs[idx], 'card');
    // Same pop the stepper avatar does (remove → reflow → add restarts it
    // when the shopper swaps photos back-to-back)
    circle.classList.remove('quiz2-avatar-pop');
    void circle.offsetWidth;
    circle.classList.add('quiz2-avatar-pop');
   }
   var btn = card.querySelector('.quiz2-upload-btn');
   if (btn && btn.firstChild && btn.firstChild.nodeType === 3) {
    btn.firstChild.nodeValue = (this.state.dogs[idx].photo ? 'Change Photo' : 'Upload Photo') + ' ';
   }
  },

  // Upload to the quiz Worker: R2 storage + Gemini breed ID in one response.
  // Fire-and-forget from the shopper's point of view — the local preview is
  // already showing, so a failure here costs nothing visible.
  uploadDogPhoto: function (idx, blob) {
   var self = this;
   if (!QUIZ_BACKEND_URL || !blob) return;
   // Per-dog token: a newer pick for the same dog supersedes this response
   this._photoSeq = this._photoSeq || {};
   var seq = (this._photoSeq[idx] = (this._photoSeq[idx] || 0) + 1);
   var fd = new FormData();
   fd.append('photo', blob, 'dog.jpg');
   fd.append('quizId', this.state.quizId);
   fd.append('dogIndex', String(idx));
   // The theme's breed list is the vocabulary Gemini must answer from
   fd.append('breeds', JSON.stringify(window.quizBreeds || []));
   fetch(QUIZ_BACKEND_URL + '/quiz/photo', { method: 'POST', body: fd })
    .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); })
    .then(function (res) {
     if (seq !== self._photoSeq[idx]) return;
     self.applyPhotoResult(idx, res);
    })
    .catch(function (e) {
     console.warn('[Kismet Quiz] photo upload failed (local preview kept):', e);
    });
  },

  applyPhotoResult: function (idx, res) {
   var dog = this.state.dogs[idx];
   if (!dog || !res || !res.ok) return;

   // Hosted URL replaces the heavy dataURL (localStorage + backend payloads).
   // Warm the browser cache first: the DOM keeps showing the identical local
   // preview, so re-fetching the same pixels must never blank the avatar.
   if (res.photoUrl) {
    dog.photo = res.photoUrl;
    new Image().src = res.photoUrl;
   }
   if (res.breed) dog.detectedBreed = res.breed;

   // Preload the breed for the basics screen — but never overwrite a breed
   // the shopper picked, nor fight the typeahead mid-typing.
   var input = document.getElementById('quiz2-breed');
   var typing = input && input === document.activeElement && input.value.trim() !== '';
   var prefilled = false;
   if (res.breed && !dog.breed && !typing) {
    dog.breed = res.breed;
    prefilled = true;
    // renderFields skips the focused input, so write the visible value here
    // (covers focusFirst() having landed on an empty breed input)
    if (input && idx === this.state.dogIndex) input.value = res.breed;
   }

   this.pushDL({
    event: 'quiz_photo_breed_detected',
    dog_index: idx + 1,
    breed: res.breed || '',
    confidence: typeof res.confidence === 'number' ? res.confidence : 0,
    breed_prefilled: prefilled
   });

   try { this.save(); } catch (e) { /* quota — same fallback as the preview */ }

   // On the start screen there is nothing visible to update — the avatar
   // already shows this photo from the local preview, and swapping its src to
   // the remote URL mid-download flickers. State is saved; the next natural
   // render uses the (pre-warmed) hosted URL.
   if (this.state.screen === 'start') return;

   // Later screens: surface the prefilled breed without stealing focus
   this.renderFields();
   this.renderSideIllustration();
   this.sizeBreedInput();
   this.renderDogNames();
   this.renderProgress();
  },

  handleFieldInput: function (el, isCommit) {
   var field = el.dataset.field;
   var value = el.value;

   if (field === 'email') {
    this.state.email = value;
   } else if (field === 'vetQuestion') {
    this.state.vetQuestion = value;
   } else if (el.dataset.dogIndex !== undefined) {
    // Start-screen name inputs carry an explicit index
    var idx = parseInt(el.dataset.dogIndex, 10);
    if (this.state.dogs[idx]) {
     // trim: a trailing space would render as "Coco 's birthday" downstream
     this.state.dogs[idx].name = value.trim().slice(0, 21);
     this.updateNameFact(idx);
     this.updateStartCta();
    }
   } else {
    this.setDogField(field, value);
    if (field === 'birthdayMonth' || field === 'birthdayDay') {
     this.renderZodiac();
     this.renderFields(); // progressive reveal: activity appears when the birthday completes
    }
    if (field === 'activity') this.renderFields(); // …and motivation appears after activity
    if (field === 'breed') {
     this.renderProgress();
     this.renderSideIllustration();
     this.renderFields(); // sync the basics typeahead input when edited from the recap
    }
    if (field === 'birthdayYear') this.renderFields(); // senior-care chip visibility (8+)
    if (field === 'motivation') {
     this.renderFields(); // reveals the "Something else" vet textarea
     if (value === 'something_else') {
      var vetTa = document.getElementById('quiz2-lifestage-vet-question');
      if (vetTa) vetTa.focus({ preventScroll: false });
     }
    }
    if (field === 'sex') {
     this.renderFields(); // reveals spay/neuter with sex-specific labels
     this.renderSummary(); // recap spay chip relabels (Spayed↔Neutered) / appears
     this.renderProgress(); // bowl recolors (male blue / female pink)
     this.renderSideIllustration(); // female art (bow) ↔ male art
    }
    if (field === 'weight') { this.sizeWeightInput(); this.sizeSumWeight(); }
   }

   this.save();
   if (el.tagName === 'SELECT') this.sizeSelects();
   this.updateContinue();
   if (isCommit) this.trackFieldCompleted(field, value);
   if (this.errors.length) { this.errors = []; this.renderErrors(); }

   // Live error for filled-but-invalid values (QA 2026-08-10): the Continue
   // hides while errors exist, so an impossible weight would otherwise fail
   // SILENTLY — there's no visible button to press to surface the message.
   // Empty fields stay quiet (the step just isn't done yet); only committed
   // out-of-range input speaks, and the next keystroke clears it (reset above).
   if (isCommit && field === 'weight' && value !== '' && window.QuizValidator) {
    var weightErrs = window.QuizValidator
     .getScreenErrors('basics', this.currentDog(), this.state)
     .filter(function (m) { return /weight/i.test(m); });
    if (weightErrs.length) { this.errors = weightErrs; this.renderErrors(); }
   }
  },

  setDogField: function (field, value) {
   var dog = this.currentDog();
   if (!dog) return;
   if (field === 'weight') {
    dog.weight = value === '' ? '' : parseFloat(value);
   } else {
    dog[field] = value;
   }
   this.save();
  },

  // ── Multi-select dropdowns (Figma "Dropdown Selected") ─────────────────
  toggleMultiPanel: function (group) {
   if (!group) return;
   var wasOpen = group.classList.contains('is-open');
   this.closeMultiPanels();
   if (wasOpen) return;
   group.classList.add('is-open');
   var panel = group.querySelector('.quiz2-multi-panel');
   if (panel) panel.removeAttribute('hidden');
   var trigger = group.querySelector('.quiz2-multi-trigger');
   if (trigger) {
    trigger.setAttribute('aria-expanded', 'true');
    // Freeze the trigger width while the panel is open: each pick rewrites
    // the summary label, and a width change reflows the sentence and drags
    // the open panel out from under the cursor. Ellipsis absorbs overflow;
    // the real width is re-applied on close.
    trigger.style.width = Math.ceil(trigger.getBoundingClientRect().width) + 'px';
   }
  },

  closeMultiPanels: function () {
   document.querySelectorAll('.quiz2-multi.is-open').forEach(function (group) {
    group.classList.remove('is-open');
    var panel = group.querySelector('.quiz2-multi-panel');
    if (panel) panel.setAttribute('hidden', '');
    var trigger = group.querySelector('.quiz2-multi-trigger');
    if (trigger) {
     trigger.setAttribute('aria-expanded', 'false');
     trigger.style.width = ''; // unfreeze — hug the final summary label
    }
   });
  },

  // The panel stays open across picks (multi-select); it closes via the
  // explicit Close row, outside click, Escape, or a screen change.
  toggleMultiOption: function (btn) {
   var group = btn.closest('[data-multi]');
   var dog = this.currentDog();
   if (!group || !dog) return;
   var field = group.dataset.multi;
   var value = btn.dataset.value;
   if (!Array.isArray(dog[field])) dog[field] = [];
   var list = dog[field];
   var isExclusive = btn.hasAttribute('data-exclusive');
   var i = list.indexOf(value);

   if (i > -1) {
    list.splice(i, 1);
   } else if (isExclusive) {
    dog[field] = [value];
   } else {
    // Selecting a normal option clears any exclusive one ("None")
    var exclusiveValues = Array.prototype.map.call(
     group.querySelectorAll('[data-exclusive]'), function (b) { return b.dataset.value; });
    dog[field] = list.filter(function (v) { return exclusiveValues.indexOf(v) === -1; });
    dog[field].push(value);
   }

   this.save();
   this.trackFieldCompleted(field, dog[field].join(','));
   if (this.errors.length) { this.errors = []; this.renderErrors(); }
   this.renderFields(); // includes updateContinue

   // P16: the moment the allergy mix rules out every recipe, block the flow —
   // no point answering more when there's nothing safe to recommend. Removing
   // the allergy unblocks it (renderVetEnd derives both ways from state).
   if (field === 'allergies') {
    if (allergiesBlockAllRecipes(dog)) { this.enterVetEnd('allergy_toggle'); }
    else { this.renderVetEnd(); }
   }
  },

  currentDog: function () {
   return this.state.dogs[this.state.dogIndex];
  },

  // ── Start screen: dogs add/remove + name facts ─────────────────────────
  handleAddDog: function () {
   if (this.state.dogs.length >= MAX_DOGS) return;
   this.state.dogs.push(emptyDog());
   this.save();
   this.renderStart();
   var inputs = document.querySelectorAll('.quiz2-dog-name-input');
   if (inputs.length) inputs[inputs.length - 1].focus();
  },

  handleRemoveDog: function (idx) {
   if (this.state.dogs.length <= 1) return;
   this.state.dogs.splice(idx, 1);
   if (this.state.dogIndex >= this.state.dogs.length) this.state.dogIndex = 0;
   this.save();
   this.renderStart();
  },

  updateNameFact: function (idx) {
   var dog = this.state.dogs[idx];
   var hintEl = document.querySelector('[data-name-fact="' + idx + '"]');
   if (!dog || !hintEl) return;
   // Exact vetted match only (PRD: silently no-match anything else).
   var key = nameFactKey(dog.name);
   var fact = (key && nameFactIndex[key]) || '';
   if (key && !fact) this.loadNameFactGroup(nameFactGroup(key));
   dog.libraryMatch = !!fact;
   hintEl.textContent = fact;
   if (fact && !dog.nameFactShown) {
    dog.nameFactShown = true;
    this.trackNameFactRevealed(dog);
   }

  },

  // ── Navigation ──────────────────────────────────────────────────────────
  screenSequence: function () {
   // Flat list: start, then 3 screens per dog, then gate.
   var seq = [{ screen: 'start', dogIndex: 0 }];
   for (var d = 0; d < this.state.dogs.length; d++) {
    for (var s = 0; s < DOG_SCREENS.length; s++) {
     seq.push({ screen: DOG_SCREENS[s], dogIndex: d });
    }
   }
   seq.push({ screen: 'gate', dogIndex: this.state.dogs.length - 1 });
   return seq;
  },

  currentSeqIndex: function () {
   var seq = this.screenSequence();
   for (var i = 0; i < seq.length; i++) {
    if (seq[i].screen === this.state.screen &&
        (this.state.screen === 'start' || this.state.screen === 'gate' || seq[i].dogIndex === this.state.dogIndex)) {
     return i;
    }
   }
   return 0;
  },

  validateCurrent: function () {
   if (!window.QuizValidator) return true;
   var dog = this.currentDog();
   var errors = [];
   var screenIdx = DOG_SCREENS.indexOf(this.state.screen);
   if (screenIdx > -1) {
    // The recap makes earlier answers editable (and clearable) from later
    // screens, so re-validate every screen up to the current one.
    for (var i = 0; i <= screenIdx; i++) {
     errors = errors.concat(window.QuizValidator.getScreenErrors(DOG_SCREENS[i], dog, this.state));
    }
   } else {
    errors = window.QuizValidator.getScreenErrors(this.state.screen, dog, this.state);
   }
   this.errors = errors;
   this.renderErrors();
   return errors.length === 0;
  },

  handleStartContinue: function () {
   this.syncStartInputs();
   if (!this.validateCurrent()) return;

   if (!this.state.startedAt) {
    this.state.startedAt = new Date().toISOString();
    this.trackQuizStarted();
    this.klaviyoIdentifyStep('start');
   }

   this.trackStepCompleted('start');
   this.state.screen = 'basics';
   this.state.dogIndex = 0;
   this.save();
   this.render();
  },

  handleContinue: function () {
   if (!this.validateCurrent()) return;

   // P16 backstop (the Continue is hidden while blocked, but a stale render
   // or saved session could still land here)
   if (this.vetBlocked()) {
    this.enterVetEnd('recipe_continue');
    return;
   }

   var dog = this.currentDog();
   var screenIdx = DOG_SCREENS.indexOf(this.state.screen);
   if (dog && screenIdx > -1) {
    dog.screensCompleted = Math.max(dog.screensCompleted || 0, screenIdx + 1);
   }

   this.trackStepCompleted(this.state.screen);
   this.klaviyoIdentifyStep('step');

   if (screenIdx < DOG_SCREENS.length - 1) {
    this.state.screen = DOG_SCREENS[screenIdx + 1];
   } else if (this.state.dogIndex < this.state.dogs.length - 1) {
    this.state.dogIndex++;
    this.state.screen = DOG_SCREENS[0];
   } else {
    this.state.screen = 'gate';
   }

   this.errors = [];
   this.save();
   this.render();
  },

  handleSwitchDog: function (idx) {
   if (idx < 0 || idx >= this.state.dogs.length) return;
   this.state.dogIndex = idx;
   var completed = this.state.dogs[idx].screensCompleted || 0;
   this.state.screen = DOG_SCREENS[Math.min(completed, DOG_SCREENS.length - 1)];
   this.errors = [];
   this.save();
   this.render();
  },

  handleSameAsDog1: function () {
   var first = this.state.dogs[0];
   var dog = this.currentDog();
   if (!first || !dog || dog === first) return;
   // PRD: shortcut for repeated household inputs — current food + allergies.
   dog.allergies = (first.allergies || []).slice();
   dog.currentFoodType = first.currentFoodType;
   dog.currentFoodBrand = first.currentFoodBrand;
   this.save();
   this.renderFields();
  },

  handleClose: function () {
   var referrer = document.referrer;
   if (referrer && referrer.indexOf(window.location.origin) === 0 &&
       referrer.indexOf('/quiz') === -1 && referrer.indexOf('view=quiz') === -1) {
    window.location.href = referrer;
    return;
   }
   window.location.href = '/';
  },

  syncStartInputs: function () {
   // Autofill / unfired-change safety: re-read the start-screen name inputs.
   var self = this;
   document.querySelectorAll('.quiz2-dog-name-input').forEach(function (input) {
    var idx = parseInt(input.dataset.dogIndex, 10);
    if (self.state.dogs[idx]) self.state.dogs[idx].name = input.value.trim().slice(0, 21);
   });
   this.save();
  },

  // ── Email gate + finish (legacy results bridge) ─────────────────────────
  // ── Ask-a-Vet ending (P16): allergies rule out every recipe ─────────────
  // Not a separate screen (Figma "Quiz / Step 3 / B"): the recipe screen
  // fades and locks, only the allergies dropdown stays live — removing an
  // allergy returns the quiz to normal — and the vet card shows below.
  enterVetEnd: function (trigger) {
   var dog = this.currentDog() || {};
   this.closeMultiPanels();
   this.renderVetEnd();
   var emailEl = document.getElementById('quiz2-vet-end-email');
   if (emailEl && !emailEl.value) emailEl.focus({ preventScroll: false });
   this.pushDL({
    event: 'quiz_vet_end_shown',
    reason: 'allergy_block',
    trigger: trigger,
    allergies: (dog.allergies || []).join(',')
   });
  },

  vetBlocked: function () {
   return this.state.screen === 'recipe' && allergiesBlockAllRecipes(this.currentDog());
  },

  renderVetEnd: function () {
   var block = document.getElementById('quiz2-vet-end');
   var screenEl = document.querySelector('[data-quiz-screen="recipe"]');
   if (!block || !screenEl) return;
   var blocked = this.vetBlocked();
   screenEl.classList.toggle('is-vet-blocked', blocked);
   if (blocked) { block.removeAttribute('hidden'); } else { block.setAttribute('hidden', ''); return; }

   var formEl = block.querySelector('[data-vet-end-form]');
   var doneEl = block.querySelector('[data-vet-end-done]');
   if (this.state.vetEndAt) {
    formEl.setAttribute('hidden', '');
    doneEl.removeAttribute('hidden');
   } else {
    formEl.removeAttribute('hidden');
    doneEl.setAttribute('hidden', '');
   }
  },

  handleVetEndSubmit: function () {
   var emailEl = document.getElementById('quiz2-vet-end-email');
   var qEl = document.getElementById('quiz2-vet-end-question');
   if (qEl) this.state.vetQuestion = qEl.value;
   if (this.emailNeedsConfirmation(emailEl, document.getElementById('quiz2-vet-end-errors'), 'vet-end')) return;

   // Errors paint inside the vet card (state.screen is still 'recipe', so the
   // generic renderErrors would route them to the recipe container instead)
   var msgs = [];
   var v = window.QuizValidator;
   if (!this.state.email || (v && !v.isValidEmail(this.state.email))) {
    msgs.push('Drop in your email so our vet team can reply');
   }
   if (!(this.state.vetQuestion || '').trim()) {
    msgs.push('Tell us a little about your pup so the vet can help');
   }
   var errEl = document.getElementById('quiz2-vet-end-errors');
   if (errEl) {
    errEl.innerHTML = msgs.length ? '<p class="quiz-error-message">' + msgs.join('<br>') + '</p>' : '';
   }
   if (msgs.length) return;

   this.state.vetEndAt = new Date().toISOString();
   this.save();
   this.rememberEmail();
   this._gateKx = readKlaviyoExchangeId();

   this.trackEmailSubmitted();
   this.pushDL({ event: 'quiz_vet_end_submitted', reason: 'allergy_block' });
   // Klaviyo: finish-style profile props flagged with the outcome — the vet
   // team gets the email via the backend; Klaviyo powers the confirmation flow
   this.klaviyoResetDraft();
   var api = this.klaviyoApi();
   if (api) {
    try {
     var props = this.klaviyoProps(true);
     props.is_abandoned = false;
     props.quiz_outcome = 'vet_end_allergy_block';
     if ((this.state.vetQuestion || '').trim()) {
      props.quiz_vet_question = this.state.vetQuestion.trim().slice(0, 1000);
     }
     api.identify(props);
    } catch (e) {
     console.error('[Kismet Quiz] Klaviyo error:', e);
    }
   }
   this.submitVetQuestionToBackend('quiz-v2-allergy-block');
   this.renderVetEnd();
  },

  // ── Email typo guard ("did you mean") ──────────────────────────────────
  // Shared by the gate and the Ask-a-Vet ending. Normalizes the typed
  // address into the input + state, and if the domain looks like a typo of
  // a known one, paints a one-tap suggestion into the error slot and returns
  // true so the caller stops. The shopper can accept it or keep theirs; a
  // kept address is remembered so the prompt shows once per address.
  emailNeedsConfirmation: function (inputEl, errEl, submitId) {
   if (!inputEl) return false;
   var v = window.QuizValidator;
   var email = v ? v.normalizeEmail(inputEl.value) : inputEl.value.trim();
   inputEl.value = email;
   this.state.email = email;
   // A previous prompt/error leaves the field red — clear before re-judging.
   inputEl.classList.remove('is-invalid');
   inputEl.removeAttribute('aria-invalid');
   if (errEl) { errEl.classList.remove('has-errors'); errEl.innerHTML = ''; }
   if (!v) return false;
   if (!v.isValidEmail(email)) return false; // the normal validation message handles it
   var suggestion = v.suggestEmail(email);
   if (!suggestion || this._emailKept === email) return false;
   if (errEl) {
    errEl.classList.add('has-errors');
    errEl.innerHTML =
     '<div class="quiz2-email-suggest" role="alert">' +
      '<p class="quiz-error-message">Did you mean <strong>' + suggestion.replace(/</g, '&lt;') + '</strong>?</p>' +
      '<div class="quiz2-email-suggest-actions">' +
       '<button type="button" class="quiz2-email-suggest-btn is-primary" data-email-suggest="accept" data-email="' + suggestion.replace(/"/g, '&quot;') + '" data-input="' + inputEl.id + '" data-submit="' + submitId + '">Yes, use that</button>' +
       '<button type="button" class="quiz2-email-suggest-btn" data-email-suggest="keep" data-email="' + email.replace(/"/g, '&quot;') + '" data-input="' + inputEl.id + '" data-submit="' + submitId + '">No, keep mine</button>' +
      '</div>' +
     '</div>';
   }
   inputEl.classList.add('is-invalid');
   this.pushDL({ event: 'quiz_email_suggestion_shown' });
   return true;
  },

  handleEmailSuggestion: function (btn) {
   var inputEl = document.getElementById(btn.dataset.input);
   var accepted = btn.dataset.emailSuggest === 'accept';
   if (inputEl) inputEl.value = btn.dataset.email;
   this.state.email = btn.dataset.email;
   if (!accepted) this._emailKept = btn.dataset.email;
   this.pushDL({ event: 'quiz_email_suggestion_' + (accepted ? 'accepted' : 'kept') });
   if (btn.dataset.submit === 'vet-end') this.handleVetEndSubmit();
   else this.handleGateSubmit();
  },

  handleGateSubmit: function () {
   var emailEl = document.getElementById('quiz2-email');
   if (this.emailNeedsConfirmation(emailEl, document.getElementById('quiz2-gate-errors'), 'gate')) return;
   if (!this.validateCurrent()) return;
   this._gateKx = readKlaviyoExchangeId();

   this.state.completedAt = new Date().toISOString();
   this.save();
   this.rememberEmail();

   this.trackEmailSubmitted();
   this.trackQuizCompleted();
   this.klaviyoResetDraft();
   this.klaviyoIdentifyFinish();
   this.submitCompletionToBackend();
   this.submitVetQuestionToBackend();

   // No intermediate "pulling together" view (client 2026-08-06: the gate
   // already says the plan is ready), and no wait either. The reveal used to
   // be held behind a race against the Klaviyo identify->track chain capped at
   // 1500ms; two serial round trips rarely fit, so navigation killed the
   // pending track and only ~35% of `Completed Quiz` events survived. The
   // event now leaves from the Worker with keepalive, so nothing here needs
   // the page to stay alive. CTA still disables against double-submits.
   var btn = document.getElementById('quiz2-gate-cta');
   if (btn) btn.disabled = true;
   this.redirectToResults();
  },

  // V2 Reveal reads the answers straight from localStorage — no answers in the
  // URL (PRD). The legacy results page and its query-string bridge are gone.
  redirectToResults: function () {
   window.location.href = '/pages/quiz-results?view=quiz-v2-results';
  },

  // ── Analytics (PRD taxonomy — no raw PII) ───────────────────────────────
  pushDL: function (payload) {
   if (typeof dataLayer === 'undefined') return;
   payload.quiz_id = this.state.quizId;
   payload.quiz_variant = QUIZ_VARIANT;
   payload.device_type = deviceType();
   payload.timestamp = new Date().toISOString();
   dataLayer.push(payload);
  },

  // Legacy event kept in parallel until GTM migrates to the new taxonomy.
  pushLegacyQuizStep: function (stepName, stepNumber, completed) {
   if (typeof dataLayer === 'undefined') return;
   dataLayer.push({
    event: 'quizStep',
    quizStepNumber: stepNumber,
    quizStepName: stepName,
    quizStatus: completed ? 'completed' : 'in_progress',
    quizTimestamp: new Date().toISOString()
   });
  },

  trackQuizStarted: function () {
   var payload = { event: 'quiz_started', dog_count: this.state.dogs.length };
   Object.assign(payload, this.state.utm);
   this.pushDL(payload);
   this.pushLegacyQuizStep('1', 1, false);
  },

  trackStepCompleted: function (screen) {
   var dog = this.currentDog() || {};
   var stepNumber = this.currentSeqIndex() + 1;
   var payload = {
    event: 'quiz_step_completed',
    step_number: stepNumber,
    step_name: screen,
    dog_index: this.state.dogIndex + 1
   };
   // Selected values where safe (no names/emails)
   if (screen === 'basics') {
    payload.breed = dog.breed;
    payload.weight = dog.weight;
    payload.body_condition = dog.bodyCondition;
   } else if (screen === 'lifestage') {
    payload.age_months = dogAgeMonths(dog);
    payload.activity = dog.activity;
    payload.primary_motivation = dog.motivation;
   } else if (screen === 'recipe') {
    payload.health_goals = (dog.healthGoals || []).join(',');
    payload.top_health_goal = topHealthGoal(dog.healthGoals);
    payload.allergies = (dog.allergies || []).join(',');
    payload.current_food_type = dog.currentFoodType;
   }
   this.pushDL(payload);
   this.pushLegacyQuizStep(String(stepNumber), stepNumber, false);
  },

  trackFieldCompleted: function (fieldName, value) {
   this.pushDL({
    event: 'quiz_field_completed',
    field_name: fieldName,
    field_status: value ? 'completed' : 'cleared'
   });
  },

  trackNameFactRevealed: function (dog) {
   var self = this;
   hashForAnalytics(dog.name).then(function (h) {
    self.pushDL({
     event: 'quiz_name_fact_revealed',
     dog_name_hash: h,
     library_match: true,
     name_fact_version: 'combined-final-v5-2026-10'
    });
   });
  },

  trackEmailSubmitted: function () {
   var self = this;
   var consentTs = new Date().toISOString();
   hashForAnalytics(this.state.email).then(function (h) {
    self.pushDL({
     event: 'quiz_email_submitted',
     email_hash: h,
     email_source: self.emailSourceProps().quiz_email_source,
     consent_timestamp: consentTs,
     consent_version: CONSENT_VERSION
    });
   });
  },

  trackQuizCompleted: function () {
   var dog = this.state.dogs[0] || {};
   this.pushDL({
    event: 'quiz_completed',
    dog_count: this.state.dogs.length,
    health_goals: (dog.healthGoals || []).join(','),
    top_health_goal: topHealthGoal(dog.healthGoals),
    avatar: resolveAvatar(dog)
   });
   this.pushLegacyQuizStep('Results', this.screenSequence().length + 1, true);
  },

  // ── Klaviyo (hybrid identify) ───────────────────────────────────────────
  klaviyoProps: function (includeEmail) {
   var self = this;
   var props = { dog_count: this.state.dogs.length, quiz_variant: QUIZ_VARIANT };
   if (includeEmail && this.state.email) props.$email = this.state.email;

   var dogsArray = [];
   this.state.dogs.forEach(function (dog, i) {
    var p = 'dog_' + (i + 1) + '_';
    if (dog.name) props[p + 'name'] = dog.name;
    if (dog.breed) {
     props[p + 'breed'] = dog.breed;
     props[p + 'breedImgUrl'] = getBreedArtUrl(dog.breed, dog.sex);
    }
    if (dog.sex) props[p + 'sex'] = dog.sex;
    if (dog.weight) props[p + 'weight'] = dog.weight;
    if (dog.bodyCondition) props[p + 'body_condition'] = dog.bodyCondition;
    if (dog.spayNeuter) props[p + 'spay_neuter'] = dog.spayNeuter;
    if (dog.birthdayMonth && dog.birthdayDay) props[p + 'birthday_month_day'] = dog.birthdayMonth + ' ' + dog.birthdayDay;
    var ageMonths = dogAgeMonths(dog);
    if (ageMonths !== null) props[p + 'age_months'] = ageMonths;
    if (dog.activity) props[p + 'activity'] = dog.activity;
    if (dog.motivation) props[p + 'primary_motivation'] = dog.motivation;
    if ((dog.healthGoals || []).length) {
     props[p + 'health_goals'] = dog.healthGoals.join(',');
     props[p + 'top_health_goal'] = topHealthGoal(dog.healthGoals);
    }
    if ((dog.allergies || []).length) props[p + 'allergies'] = dog.allergies.join(',');
    if (dog.currentFoodType) props[p + 'current_food_type'] = dog.currentFoodType;
    if (dog.currentFoodBrand) props[p + 'current_food_brand'] = dog.currentFoodBrand;

    dogsArray.push({ name: dog.name || '', breed: dog.breed || '', avatar_url: getBreedFaceUrl(dog.breed) });
   });

   // Klaviyo email-header contract (existing dynamic emails): dogs array + greeting.
   props.dogs = dogsArray;
   props.dogs_display_name = buildDogsDisplayName(dogsArray.map(function (d) { return d.name; }));
   props.avatar = resolveAvatar(this.state.dogs[0] || {});
   return props;
  },

  klaviyoApi: function () {
   var hasNew = window.klaviyo && typeof window.klaviyo.identify === 'function';
   var hasLegacy = window._learnq && Array.isArray(window._learnq);
   if (!hasNew && !hasLegacy) return null;
   return {
    identify: function (props) {
     if (hasNew) return window.klaviyo.identify(props);
     window._learnq.push(['identify', props]);
     return Promise.resolve();
    },
    track: function (name, props) {
     if (hasNew) return window.klaviyo.track(name, props);
     window._learnq.push(['track', name, props]);
     return Promise.resolve();
    }
   };
  },

  // Pre-gate ("draft") profile props. Before the gate there is no confirmed
  // email: the identify lands on whichever profile owns the Klaviyo cookie,
  // which is not always the person who finishes the quiz (shared device,
  // different address at the gate). So this writes ONLY what the abandonment
  // emails render (`person.dogs.N.name` / `.avatar_url`, verified against the
  // live flow templates 2026-09-21) plus quiz metadata. The full `dog_N_*`
  // answers are written once, at completion, to the confirmed email — so a
  // draft can never overwrite someone else's finished quiz.
  klaviyoDraftProps: function () {
   var full = this.klaviyoProps(false);
   return {
    dog_count: full.dog_count,
    quiz_variant: full.quiz_variant,
    dogs: full.dogs,
    dogs_display_name: full.dogs_display_name,
    is_abandoned: true,
    quiz_last_step: this.state.screen,
    quiz_list_id: KLAVIYO_QUIZ_LIST_ID,
    quiz_started_at: this.state.startedAt,
    // Whether a Kismet customer was logged in on the page when this draft
    // was written ('account') or Klaviyo only knew the browser by its cookie,
    // from an earlier form or email click ('cookie'). Asked by Retencity
    // (2026-10-29) to narrow an abandonment segment. Describes the page, not
    // which profile the cookie points to; drafts written before this shipped
    // don't have it.
    quiz_identified_via: window.quizCustomerEmail ? 'account' : 'cookie',
    // Which attempt wrote this draft. The Worker's delayed clear only fires
    // if the cookie profile still carries THIS id (see docs/quiz-klaviyo.md).
    quiz_draft_id: this.state.quizId
   };
  },

  // Event payload shared by Started/Completed Quiz so the two events (and the
  // `quiz` order tag) can be joined on quiz_id.
  eventIds: function () {
   var ids = { quiz_id: this.state.quizId, quiz_variant: QUIZ_VARIANT };
   var utm = this.state.utm || {};
   Object.keys(utm).forEach(function (k) { ids[k] = utm[k]; });
   return ids;
  },

  // Per-step: ONLY for profiles Klaviyo already knows (cookie). Never sends email.
  klaviyoIdentifyStep: function (kind) {
   if (!this.klaviyoKnown) return;
   var api = this.klaviyoApi();
   if (!api) return;
   try {
    var props = this.klaviyoDraftProps();
    if (kind === 'start') {
     var eventProps = Object.assign({ dog_count: props.dog_count }, this.eventIds());
     api.identify(props).then(function () {
      api.track('Started Quiz', eventProps);
     });
    } else {
     api.identify(props);
    }
    this.state.klaviyoDraftWritten = true;
    this.state.klaviyoDraftKx = readKlaviyoExchangeId() || this.state.klaviyoDraftKx || '';
    this.save();
   } catch (e) {
    console.error('[Kismet Quiz] Klaviyo error:', e);
   }
  },

  // 4) Gate reset. Fired BEFORE the identify that carries the email, while the
  // cookie still points at the profile the draft identifies wrote to. If that
  // profile is the one finishing the quiz, completion writes the same values
  // and ordering is irrelevant; if it is someone else, they stop looking
  // abandoned. The abandonment flow must filter on `is_abandoned` for this to
  // matter (requested from the agency 2026-09-21). Without draft identifies
  // there is nothing to reset.
  klaviyoResetDraft: function () {
   if (!this.klaviyoKnown) return;
   var api = this.klaviyoApi();
   if (!api) return;
   try {
    api.identify({ is_abandoned: false, quiz_last_step: 'gate' });
   } catch (e) {
    console.error('[Kismet Quiz] Klaviyo error:', e);
   }
  },

  // The completion profile properties. Shared by the onsite identify below and
  // by the Worker payload, so both describe the profile identically.
  completionProps: function () {
   var props = this.klaviyoProps(true);
   props.is_abandoned = false;
   // Overwrites a stale 'vet_end_allergy_block' if the shopper removed the
   // blocking allergy and finished normally — flows segment on this flag
   // (client 2026-08-12), so a completed retake must clear the block state.
   props.quiz_outcome = 'completed';
   props.quiz_last_step = 'gate';
   props.quiz_draft_id = this.state.quizId; // supersedes any pending clear
   props.quiz_list_id = KLAVIYO_QUIZ_LIST_ID;
   props.quiz_started_at = this.state.startedAt;
   props.quiz_completed_at = this.state.completedAt;
   Object.assign(props, this.recommendationProps());
   // Optional Ask-a-Vet question (Figma gate). Klaviyo-only — free text never
   // goes to the dataLayer. Routing to the vet team TBD (flagged to client).
   if ((this.state.vetQuestion || '').trim()) {
    props.quiz_vet_question = this.state.vetQuestion.trim().slice(0, 1000);
   }
   return props;
  },

  // Result-level outputs (Retencity 2026-09-22): what the Reveal will show,
  // computed here from the answers via the shared resolver so the Completed
  // Quiz event carries route / recipe / topper / bag / cadence per dog.
  // `quiz_route` / `quiz_recipe` mirror dog 1 for single-dog flows.
  recommendationProps: function () {
   var out = {};
   var R = window.QuizResolver;
   if (!R) return out;
   this.state.dogs.forEach(function (dog, i) {
    var p = 'dog_' + (i + 1) + '_';
    var r;
    try { r = R.resolve(dog); } catch (e) { return; }
    out[p + 'route'] = r.routeKey;
    out[p + 'recipe'] = r.recipeKey || 'none';
    out[p + 'recipe_label'] = r.recipeLabel;
    out[p + 'topper'] = r.topper || 'none';
    out[p + 'treats'] = r.treats;
    out[p + 'bag_lb'] = parseInt(r.bagWeight, 10);
    out[p + 'cadence_weeks'] = r.cadence;
    out[p + 'calories_day'] = r.calories;
    out[p + 'weight_band'] = r.band;
    if (i === 0) {
     out.quiz_route = r.routeKey;
     out.quiz_recipe = r.recipeKey || 'none';
    }
   });
   return out;
  },

  // Event payload = the completion profile props plus the ids that make the
  // instance traceable (quiz_id ↔ D1 row ↔ cart attribute ↔ order tag).
  completionEventProps: function () {
   var props = Object.assign(this.completionProps(), this.eventIds(), this.emailSourceProps());
   delete props.$email;
   return props;
  },

  // Gate: onsite identify, fire-and-forget. This exists for the browser-side
  // link (it binds the __kla_id cookie to the profile, which onsite tracking
  // and the abandonment flow rely on) — NOT for the properties or the event.
  // Those travel server-side, so it is fine for the navigation to cut this off.
  klaviyoIdentifyFinish: function () {
   var api = this.klaviyoApi();
   if (!api) return;
   try {
    api.identify(this.completionProps());
   } catch (e) {
    console.error('[Kismet Quiz] Klaviyo error:', e);
   }
  },

  // Gate context for the Worker (Retencity 2026-09-22): the consent the gate
  // copy asserts (→ Quiz list subscription), the cookie profile to reconcile
  // and whether any draft identify actually ran. `_gateKx` is captured at the
  // top of the submit handlers, before the identify calls.
  gateContext: function () {
   return {
    consent: true,
    consentVersion: CONSENT_VERSION,
    kx: this._gateKx || this.state.klaviyoDraftKx || '',
    draftWritten: !!this.klaviyoKnown || !!this.state.klaviyoDraftWritten
   };
  },

  // `Completed Quiz` leaves from the Worker, which emits it with the profile
  // properties attached (Klaviyo upserts them as part of event ingestion). The
  // browser used to own this and lost ~65% of the events to the redirect.
  // keepalive: the gate navigates on the next line — without it the browser
  // cancels the request mid-navigation, which is the bug this replaces.
  submitCompletionToBackend: function () {
   if (!QUIZ_BACKEND_URL || !this.state.email) return;
   try {
    fetch(QUIZ_BACKEND_URL + '/quiz/completed', {
     method: 'POST',
     headers: { 'Content-Type': 'application/json' },
     keepalive: true,
     body: JSON.stringify(Object.assign({
      quizId: this.state.quizId,
      email: this.state.email,
      profileProperties: this.completionProps(),
      // Agency request 2026-09-21: the quiz data also travels as the event
      // payload (`event.*` in flows, trigger filters) — the profile keeps the
      // current answers, the event keeps this instance's snapshot.
      eventProperties: this.completionEventProps(),
      website: '' // honeypot — always empty from the real quiz
     }, this.gateContext()))
    }).catch(function (e) { console.error('[Kismet Quiz] completion error:', e); });
   } catch (e) {
    console.error('[Kismet Quiz] completion error:', e);
   }
  },

  // ── Quiz backend (Ask-a-Vet) ────────────────────────────────────────────
  // Fire-and-forget POST to the kismet-quiz-backend Worker: D1 record + email
  // to the vet team + Klaviyo event. Must never block or fail the gate — the
  // quiz_vet_question Klaviyo property above stays as the fallback.
  submitVetQuestionToBackend: function (source) {
   var question = (this.state.vetQuestion || '').trim();
   if (!QUIZ_BACKEND_URL || !question || !this.state.email) return;
   try {
    fetch(QUIZ_BACKEND_URL + '/quiz/ask-a-vet', {
     method: 'POST',
     headers: { 'Content-Type': 'application/json' },
     // keepalive: the gate redirects to the Reveal right after; without it
     // the browser may cancel the request mid-navigation.
     keepalive: true,
     body: JSON.stringify(Object.assign({
      quizId: this.state.quizId,
      email: this.state.email,
      question: question.slice(0, 1000),
      source: source || 'quiz-v2-gate',
      // A dataURL photo (upload failed / Worker off) would blow the backend's
      // 20k-char dogs cap — only hosted URLs travel.
      dogs: this.state.dogs.map(function (d) {
       return d.photo && d.photo.indexOf('data:') === 0 ? Object.assign({}, d, { photo: '' }) : d;
      }),
      utm: this.state.utm || undefined,
      website: '' // honeypot — always empty from the real quiz
     }, this.gateContext()))
    }).catch(function (e) { console.error('[Kismet Quiz] backend error:', e); });
   } catch (e) {
    console.error('[Kismet Quiz] backend error:', e);
   }
  },

  // ── Rendering ───────────────────────────────────────────────────────────
  render: function () {
   var self = this;
   if (this._ddClose) this._ddClose(); // stale dropdown panel on screen change
   this.closeMultiPanels();

   // Gate: warm the reveal's breed-face avatar (its only dynamic image) so
   // the results page paints without waiting on the CDN
   if (this.state.screen === 'gate' && !this._facePrefetched) {
    this._facePrefetched = true;
    var dog0 = this.state.dogs[0];
    if (dog0 && dog0.breed && !dog0.photo) {
     new Image().src = getBreedFaceUrl(dog0.breed);
    }
   }
   document.querySelectorAll('[data-quiz-screen]').forEach(function (el) {
    if (el.dataset.quizScreen === self.state.screen) {
     el.removeAttribute('hidden');
    } else {
     el.setAttribute('hidden', '');
    }
   });

   var footer = document.getElementById('quiz2-footer');
   if (footer) {
    // Footer (progress stepper) only on the per-dog screens — not on the
    // coral screens (start / gate "plan is ready" / loading)
    var showFooter = DOG_SCREENS.indexOf(this.state.screen) > -1;
    if (showFooter) { footer.removeAttribute('hidden'); } else { footer.setAttribute('hidden', ''); }
   }

   if (this.state.screen === 'start') this.renderStart();
   this.renderSideIllustration();
   this.renderDogNames();
   this.renderSummary();
   this.renderFields();
   this.renderProgress();
   this.renderZodiac();
   this.renderDogSwitcher();
   this.renderErrors();
   this.renderVetEnd();
   this.focusFirst();
  },

  renderStart: function () {
   var container = document.getElementById('quiz2-dog-cards');
   if (!container) return;
   var self = this;
   var html = '';
   this.state.dogs.forEach(function (dog, i) {
    html +=
     '<div class="quiz2-dog-card">' +
      (self.state.dogs.length > 1
       ? '<button type="button" class="quiz2-dog-remove" data-remove-dog="' + i + '" aria-label="Remove dog">&times;</button>'
       : '') +
      '<div class="quiz2-dog-card-art">' +
       '<div class="quiz2-dog-circle">' + self.dogAvatarHtml(dog, 'card') + '</div>' +
       (ENABLE_PHOTO_UPLOAD
        ? '<button type="button" class="quiz2-upload-btn" data-upload-photo="' + i + '">' +
           (dog.photo ? 'Change Photo' : 'Upload Photo') +
           ' <svg viewBox="0 0 18 18" width="18" height="18" fill="none" aria-hidden="true"><path d="M2 18C1.45 18 0.979 17.804 0.588 17.413C0.196 17.021 0 16.55 0 16V2C0 1.45 0.196 0.979 0.588 0.588C0.979 0.196 1.45 0 2 0H16C16.55 0 17.021 0.196 17.413 0.588C17.804 0.979 18 1.45 18 2V16C18 16.55 17.804 17.021 17.413 17.413C17.021 17.804 16.55 18 16 18H2ZM2 16H16V2H2V16ZM3 14H15L11.25 9L8.25 13L6 10L3 14Z" fill="currentColor"/></svg>' +
          '</button>'
        : '') +
       '<img class="quiz2-dog-bowl" src="' + BOWL_IMG_URL + '" alt="" aria-hidden="true">' +
      '</div>' +
      '<div class="quiz2-name-fact" data-name-fact="' + i + '"></div>' +
      '<input type="text" class="quiz2-pill quiz2-pill-input quiz2-dog-name-input" data-field="name" data-dog-index="' + i + '"' +
       ' placeholder="Enter your dog’s name" maxlength="21" autocomplete="off" autocapitalize="words" aria-label="Dog ' + (i + 1) + ' name"' +
       ' value="' + (dog.name || '').replace(/"/g, '&quot;') + '">' +
     '</div>';
   });
   // Upload helper (Figma start card): OUTSIDE the cards — inside, its height
   // moved the card's bottom edge, which is what the fact chip's `bottom`
   // anchors to on desktop (the chip ended up behind the input).
   if (ENABLE_PHOTO_UPLOAD) {
    html += '<p class="quiz2-upload-hint"><span>Upload a photo, and we’ll identify their breed and size when possible.</span></p>';
   }
   container.innerHTML = html;
   this.state.dogs.forEach(function (dog, i) { self.updateNameFact(i); });
   this.updateStartCta();
  },

  // Start CTA stays disabled until every dog has a name of 2+ characters
  updateStartCta: function () {
   var cta = document.getElementById('quiz2-start-cta');
   if (!cta) return;
   var ready = this.state.dogs.every(function (d) {
    return (d.name || '').trim().length >= 2;
   });
   cta.disabled = !ready;
  },

  dogAvatarHtml: function (dog, variant) {
   // Uploaded photo wins everywhere (card, progress, gate).
   // For now it's a browser-local compressed preview (canvas → dataURL) — the
   // upload/breed-ID backend (Worker + R2) will replace this with a hosted URL.
   if (dog && dog.photo) {
    return '<img class="quiz2-avatar-photo" src="' + dog.photo + '" alt="">';
   }
   if (dog && dog.breed) {
    // The start-card circle shows the FACE avatar too (QA 2026-08-11 — the
    // full-body art read badly cropped in the circle), just at 2× for its size
    var url = variant === 'card'
     ? getBreedFaceUrl(dog.breed, 400)
     : getBreedFaceUrl(dog.breed);
    // onload class → the stepper avatar fades the face in when it actually
    // arrives instead of popping mid-download
    return '<img src="' + url + '" alt="" onload="this.classList.add(\'is-in\')" onerror="this.style.display=\'none\'">';
   }
   // No breed yet: the start card shows the grey dogs placeholder (Figma);
   // small circles (progress/gate) fall back to the dog's initial.
   if (variant === 'card') {
    return '<img class="quiz2-avatar-placeholder" src="' + AVATAR_PLACEHOLDER_URL + '" alt="">';
   }
   var letter = dog && dog.name ? dog.name.trim().charAt(0).toUpperCase() : '?';
   return '<span class="quiz2-avatar-letter">' + letter + '</span>';
  },

  // Inline rename: click the name in a sentence → editable input in place.
  // The name is captured at the start screen, but there's no back button, so
  // this is the way to fix a typo later.
  editDogName: function (span) {
   if (span._editing) return;
   var self = this;
   var dog = this.currentDog();
   if (!dog) return;
   var prev = dog.name || '';
   span._editing = true;

   var input = document.createElement('input');
   input.type = 'text';
   input.className = 'quiz2-name-edit';
   input.maxLength = 21;
   input.value = prev;
   span.textContent = '';
   span.appendChild(input);

   function size() { input.style.width = Math.max(3, input.value.length + 1) + 'ch'; }
   size();
   input.focus();
   input.select();

   function commit() {
    span._editing = false;
    dog.name = (input.value.trim() || prev).slice(0, 21); // don't allow clearing
    self.save();
    self.render();
   }

   input.addEventListener('input', function () {
    dog.name = input.value.trim().slice(0, 21);
    // live-update the other name spans in the sentence
    document.querySelectorAll('[data-dog-name]').forEach(function (el) {
     if (el !== span) el.textContent = dog.name || 'your dog';
    });
    size();
   });
   input.addEventListener('blur', commit);
   input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { input.value = prev; input.blur(); }
   });
  },

  renderDogNames: function () {
   var dog = this.currentDog() || {};
   var name = (dog.name || 'your dog').trim(); // old saved sessions may carry a trailing space
   document.querySelectorAll('[data-dog-name]').forEach(function (el) {
    if (el._editing) return; // don't clobber an active rename input
    el.textContent = name;
   });
   var pronoun = dog.sex === 'male' ? 'He' : dog.sex === 'female' ? 'She' : ((dog.name || '').trim() || 'Your dog');
   document.querySelectorAll('[data-dog-pronoun]').forEach(function (el) { el.textContent = pronoun; });
   var d1 = this.state.dogs[0];
   document.querySelectorAll('[data-dog1-name]').forEach(function (el) { el.textContent = (d1 && d1.name) || 'Dog 1'; });
   var displayName = buildDogsDisplayName(this.state.dogs.map(function (d) { return d.name; })) || 'Your dog';
   document.querySelectorAll('[data-dogs-display-name]').forEach(function (el) { el.textContent = displayName; });

   var gateAvatar = document.getElementById('quiz2-gate-avatar');
   if (gateAvatar) gateAvatar.innerHTML = this.dogAvatarHtml(dog, 'face');
  },

  // Mad-libs recap of previous answers, shown on top of each per-dog screen.
  // Per the Figma, every recap value is an EDITABLE mini control (that's why the
  // flow has no back button): native selects + a number input, bound to the
  // current dog through the same delegated [data-field] change handling.
  renderSummary: function () {
   var dog = this.currentDog();
   var screen = this.state.screen;
   document.querySelectorAll('[data-summary-slot]').forEach(function (el) { el.innerHTML = ''; });
   if (!dog || DOG_SCREENS.indexOf(screen) < 1) return;

   var slot = document.querySelector('[data-quiz-screen="' + screen + '"] [data-summary-slot]');
   if (!slot) return;

   function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
   }
   function sel(field, options, current, placeholder, maxCh) {
    var html = '<select class="quiz2-sum-chip" data-field="' + field + '" aria-label="' + esc(placeholder) + '"' +
     (maxCh ? ' style="max-width:' + maxCh + 'ch"' : '') + '>';
    if (!current) html += '<option value="" selected>' + esc(placeholder) + '</option>';
    options.forEach(function (o) {
     html += '<option value="' + esc(o.value) + '"' + (String(o.value) === String(current) ? ' selected' : '') + '>' +
      esc(o.label) + '</option>';
    });
    return html + '</select>';
   }
   function opts(pairs) {
    return Object.keys(pairs).map(function (k) { return { value: k, label: pairs[k] }; });
   }

   var breedOpts = (window.quizBreeds || []).map(function (b) { return { value: b, label: b }; });
   var monthOpts = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    .map(function (m) { return { value: m, label: m }; });
   var dayOpts = [];
   for (var d = 1; d <= 31; d++) dayOpts.push({ value: d, label: d });

   // Client grammar pass 2026-08-13: "Jack is a male 30lb Field Spaniel
   // looking just right, and not neutered." — sex before weight/breed,
   // attributive "30lb", comma before the spay clause, closing period.
   var line1 = '<span data-dog-name>' + esc(dog.name || 'Your dog') + '</span> is a ' +
    sel('sex', opts({ male: 'male', female: 'female' }), dog.sex, 'Sex?', 8) +
    ', <span class="quiz2-sum-weight"><input type="number" class="quiz2-sum-chip quiz2-sum-input" data-field="weight"' +
     ' value="' + esc(dog.weight || '') + '" min="1" max="250" inputmode="numeric" aria-label="Weight in pounds">lb</span> ' +
    sel('breed', breedOpts, dog.breed, 'Breed?', 14) +
    ' looking ' +
    sel('bodyCondition', opts({ 'A little skinny.': 'a little skinny', 'Just right.': 'just right', 'A little chubby.': 'a little chubby' }),
     dog.bodyCondition, 'Shape?', 15);
   // Spay/neuter only exists once a sex is chosen (it names the procedure —
   // never "fixed"), mirroring the basics screen.
   if (dog.sex === 'male' || dog.sex === 'female') {
    var isFemale = dog.sex === 'female';
    line1 += ', and ' + sel('spayNeuter',
     opts(isFemale ? { yes: 'spayed', no: 'not spayed' } : { yes: 'neutered', no: 'not neutered' }),
     dog.spayNeuter, isFemale ? 'spayed?' : 'neutered?', 13);
   }
   line1 += '.';
   var html = '<p>' + line1 + '</p>';

   if (screen === 'recipe') {
    var yearOpts = [];
    var thisYear = new Date().getFullYear();
    for (var y = thisYear; y >= thisYear - 26; y--) yearOpts.push({ value: y, label: y });
    // Client grammar pass 2026-08-13: "He is 2 years old, born Mar 2, 2024,
    // a couch potato, and is switching because of tummy and gut issues".
    // Pronoun by sex; the dog's name when sex is unset. "Something else"
    // drops the switching clause entirely (the free-text box covers it).
    var pronoun = dog.sex === 'male' ? 'He' : dog.sex === 'female' ? 'She' : esc(dog.name || 'Your dog');
    var line2 = pronoun + ' is ' +
     (ageLabel(dog) ? esc(ageLabel(dog)) + ', born ' : 'born ') +
     sel('birthdayMonth', monthOpts, dog.birthdayMonth, 'Month?', 8) +
     sel('birthdayDay', dayOpts, dog.birthdayDay, 'Day?', 6) +
     ', ' + sel('birthdayYear', yearOpts, dog.birthdayYear, 'Year?', 7) +
     ', ' + sel('activity', opts({ 'an Olympic athlete': 'an Olympic athlete', 'pretty active': 'pretty active', 'a couch potato': 'a couch potato' }),
      dog.activity, 'Activity?', 16);
    if (dog.motivation !== 'something_else') {
     line2 += ', and is switching because of ' + sel('motivation', opts({
      gut: 'tummy and gut issues',
      price: 'too expensive',
      senior: 'senior slowing down',
      kibble_guilt: 'kibble guilt',
      labels: 'I read labels',
      new_puppy: 'new puppy',
      something_else: 'something else'
     }), dog.motivation, 'Why?', 18);
    }
    line2 += '.';
    html += '<p>' + line2 + '</p>';
   }
   slot.innerHTML = html;
   this.sizeSelects();
   this.sizeSumWeight();
  },

  // Recap weight input hugs its value (empty keeps the CSS width)
  sizeSumWeight: function () {
   var input = document.querySelector('.quiz2-sum-input[data-field="weight"]');
   if (!input) return;
   if (!input.value) { input.style.width = ''; return; }
   var meas = this.measSpan();
   var cs = getComputedStyle(input);
   meas.style.font = cs.font;
   meas.style.letterSpacing = cs.letterSpacing;
   meas.textContent = input.value;
   input.style.width = Math.ceil(meas.getBoundingClientRect().width +
    parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + 4) + 'px';
  },

  renderFields: function () {
   var dog = this.currentDog();
   if (!dog) return;

   // Inputs + selects for the current dog
   document.querySelectorAll('[data-quiz-screen] [data-field]').forEach(function (el) {
    var f = el.dataset.field;
    if (el.dataset.dogIndex !== undefined) return; // start cards handled separately
    if (f === 'email' || f === 'vetQuestion') return; // quiz-level, synced below
    if (dog[f] === undefined) return;
    if (el === document.activeElement && el.tagName === 'INPUT') return;
    // Don't wipe in-progress breed typing: only write back a confirmed breed
    if (f === 'breed' && !dog.breed) return;
    if (el.tagName === 'INPUT' || el.tagName === 'SELECT') el.value = dog[f];
   });

   var emailEl = document.getElementById('quiz2-email');
   if (emailEl && emailEl !== document.activeElement) emailEl.value = this.state.email || '';
   var vetEndEmail = document.getElementById('quiz2-vet-end-email');
   if (vetEndEmail && vetEndEmail !== document.activeElement) vetEndEmail.value = this.state.email || '';
   // All vet textareas (lifestage inline + vet-end + gate) mirror the same state field
   var self2 = this;
   document.querySelectorAll('textarea[data-field="vetQuestion"]').forEach(function (el) {
    if (el !== document.activeElement) el.value = self2.state.vetQuestion || '';
   });

   // Multi-select dropdowns: option states + trigger summary label
   document.querySelectorAll('[data-multi]').forEach(function (group) {
    var list = dog[group.dataset.multi] || [];
    var labels = [];
    group.querySelectorAll('.quiz2-multi-option').forEach(function (opt) {
     var selected = list.indexOf(opt.dataset.value) > -1;
     opt.classList.toggle('selected', selected);
     opt.setAttribute('aria-selected', selected ? 'true' : 'false');
     if (selected) labels.push(opt.textContent.trim());
    });
    var labelEl = group.querySelector('[data-multi-label]');
    if (labelEl) {
     // Figma: goals shows first selection + "+N" ("Skin and coat +1");
     // allergies shows the full list, ellipsized by CSS ("Chicken, Beef, Fi…")
     var summary;
     if (!labels.length) {
      summary = group.dataset.placeholder || '';
     } else if (group.dataset.multi === 'healthGoals') {
      summary = labels[0] + (labels.length > 1 ? ' +' + (labels.length - 1) : '');
     } else {
      summary = labels.join(', ');
     }
     labelEl.textContent = summary;
    }
    var trigger = group.querySelector('.quiz2-multi-trigger');
    if (trigger) trigger.classList.toggle('has-value', labels.length > 0);
   });

   // Senior-care goal only for seniors (PRD: "Senior care if age eight plus")
   var seniorChip = document.querySelector('[data-requires-senior]');
   if (seniorChip) {
    var isSenior = isSeniorDog(dog);
    if (isSenior) { seniorChip.removeAttribute('hidden'); } else { seniorChip.setAttribute('hidden', ''); }
    if (!isSenior && (dog.healthGoals || []).indexOf('senior_care') > -1) {
     dog.healthGoals = dog.healthGoals.filter(function (g) { return g !== 'senior_care'; });
     this.save && this.save();
    }
   }

   // Spayed/neutered only appears once a sex is chosen, with sex-specific labels
   var spaySlot = document.querySelector('[data-quiz-screen="basics"] [data-select="spayNeuter"]');
   var spaySel = document.getElementById('quiz2-spay');
   if (spaySlot && spaySel) {
    if (dog.sex === 'male' || dog.sex === 'female') {
     var spayLabels = dog.sex === 'male'
      ? { '': 'Neutered?', yes: 'Neutered', no: 'Not neutered' }
      : { '': 'Spayed?', yes: 'Spayed', no: 'Not spayed' };
     Array.prototype.forEach.call(spaySel.options, function (o) {
      if (spayLabels[o.value] !== undefined) o.textContent = spayLabels[o.value];
     });
     spaySlot.removeAttribute('hidden');
    } else {
     spaySlot.setAttribute('hidden', '');
    }
   }

   // Lifestage progressive reveal (QA 2026-08-11): the questions arrive one at
   // a time — birthday first, then activity, then motivation
   var bdayDone = dog.birthdayMonth && dog.birthdayDay && dog.birthdayYear;
   var actLine = document.querySelector('[data-quiz-screen="lifestage"] [data-reveal-activity]');
   if (actLine) {
    if (bdayDone) { actLine.removeAttribute('hidden'); } else { actLine.setAttribute('hidden', ''); }
   }
   var motSlot = document.querySelector('[data-quiz-screen="lifestage"] [data-select="motivation"]');
   if (motSlot) {
    if (bdayDone && dog.activity) { motSlot.removeAttribute('hidden'); } else { motSlot.setAttribute('hidden', '');
    }
   }

   // "Something else" motivation reveals the early Ask-a-Vet textarea
   var vetInline = document.getElementById('quiz2-something-else');
   if (vetInline) {
    if (dog.motivation === 'something_else') {
     vetInline.removeAttribute('hidden');
    } else {
     vetInline.setAttribute('hidden', '');
    }
   }

   // "Same as Dog 1?" only on recipe screens of dogs 2+
   var sameAs = document.getElementById('quiz2-same-as');
   if (sameAs) {
    if (this.state.screen === 'recipe' && this.state.dogIndex > 0) {
     sameAs.removeAttribute('hidden');
    } else {
     sameAs.setAttribute('hidden', '');
    }
   }

   this.sizeSelects();
   this.sizeBreedInput();
   this.sizeWeightInput();
   this.updateContinue();
  },

  // Continue only shows once the current screen is fully answered (Figma:
  // the button exists only in the all-answered frames). visibility keeps the
  // layout stable so nothing jumps when it appears.
  updateContinue: function () {
   var screenEl = document.querySelector('[data-quiz-screen="' + this.state.screen + '"]');
   if (!screenEl) return;
   var btn = screenEl.querySelector('[data-continue]');
   if (!btn) return;
   var errs = window.QuizValidator
    ? window.QuizValidator.getScreenErrors(this.state.screen, this.currentDog(), this.state)
    : [];
   btn.classList.toggle('is-waiting', errs.length > 0);
  },

  measSpan: function () {
   var meas = this._measSpan;
   if (!meas) {
    meas = document.createElement('span');
    meas.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;top:-9999px;';
    document.body.appendChild(meas);
    this._measSpan = meas;
   }
   return meas;
  },

  // Weight input hugs the typed number so value + "lbs" sit centered in the
  // pill as one unit (designer request); placeholder state keeps CSS width.
  sizeWeightInput: function () {
   var input = document.getElementById('quiz2-weight');
   if (!input || input.offsetParent === null) return;
   if (!input.value) { input.style.width = ''; return; }
   var meas = this.measSpan();
   var cs = getComputedStyle(input);
   meas.style.font = cs.font;
   meas.style.letterSpacing = cs.letterSpacing;
   meas.textContent = input.value;
   input.style.width = Math.ceil(meas.getBoundingClientRect().width + 4) + 'px';
  },

  // Breed input grows with its content (value or placeholder) instead of a
  // fixed width, so the pill hugs the breed name like the selects do.
  sizeBreedInput: function () {
   var input = document.getElementById('quiz2-breed');
   if (!input || input.offsetParent === null) return;
   var meas = this.measSpan();
   var cs = getComputedStyle(input);
   meas.style.font = cs.font;
   meas.style.letterSpacing = cs.letterSpacing;
   meas.textContent = input.value || input.placeholder || '';
   var w = meas.getBoundingClientRect().width +
    parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
   input.style.width = Math.ceil(w + 4) + 'px'; // +4 for the caret
  },

  // Native selects size to their WIDEST option; the design wants pills hugging
  // the selected value. Measure the current option's text and set the width.
  // Covers the recap chips too (same widest-option problem, smaller scale).
  sizeSelects: function () {
   var meas = this.measSpan();
   document.querySelectorAll('.quiz2-select select, select.quiz2-sum-chip').forEach(function (sel) {
    var opt = sel.options[sel.selectedIndex];
    if (!opt) return;
    var cs = getComputedStyle(sel);
    meas.style.font = cs.font;
    meas.style.letterSpacing = cs.letterSpacing;
    meas.textContent = opt.textContent;
    var w = meas.getBoundingClientRect().width +
     parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    sel.style.width = Math.ceil(w) + 'px';
   });
  },

  renderProgress: function () {
   // Progress = current POSITION in the flat sequence (start → per-dog
   // screens → gate), so the bar moves on every screen change — including
   // recap edits and dev-shortcut jumps, which never touch screensCompleted.
   var seq = this.screenSequence();
   var idx = this.currentSeqIndex();

   var fill = document.getElementById('quiz2-progress-fill');
   if (fill) {
    // On the first per-dog screen idx is already 1 → the bar is never empty
    // mid-quiz (design); it only hits 100% at the finish.
    var pct = this.state.completedAt ? 100 : Math.round((idx / (seq.length - 1)) * 100);
    fill.style.width = Math.min(100, Math.max(0, pct)) + '%';
   }

   var avatar = document.getElementById('quiz2-progress-avatar');
   if (avatar) {
    var avatarHtml = this.dogAvatarHtml(this.currentDog(), 'face');
    // Only touch the DOM when the avatar actually changes (renderProgress runs
    // on every render) — and greet the new face with a soft pop
    if (avatar._html !== avatarHtml) {
     avatar._html = avatarHtml;
     avatar.innerHTML = avatarHtml;
     avatar.classList.remove('quiz2-avatar-pop');
     void avatar.offsetWidth; // restart the animation on back-to-back swaps
     avatar.classList.add('quiz2-avatar-pop');
    }
   }

   // The bowl fills as the quiz advances: basics → empty, lifestage → step 2,
   // recipe → full (subtle cross-fade, see .quiz2-bowl-stage). The gate has
   // no footer, so recipe is the last visible stage.
   var bowl = document.getElementById('quiz2-progress-bowl');
   if (bowl) {
    bowl.dataset.stage = this.state.screen === 'basics' ? '1'
     : this.state.screen === 'lifestage' ? '2' : '3';
    // Bowl color follows the dog's sex: blue (male) / pink (female) / cream
    var bowlDog = this.currentDog();
    var sex = bowlDog && bowlDog.sex;
    bowl.dataset.bowlVariant = sex === 'male' || sex === 'female' ? sex : 'default';
   }
  },

  // Full-body breed illustration beside the per-dog screens (Figma: left side).
  // Preload + fade so breed changes don't flash a broken/empty image. The image
  // is rendered twice: main (left) + wrap-around slice at the right edge — the
  // tablet split behavior itself is pure CSS (see .quiz2-dog-side rules).
  renderSideIllustration: function () {
   var els = [document.getElementById('quiz2-dog-side'), document.getElementById('quiz2-dog-side-wrap')].filter(Boolean);
   if (!els.length) return;
   var dog = this.currentDog();
   var show = DOG_SCREENS.indexOf(this.state.screen) > -1 && dog && dog.breed;
   if (!show) {
    els.forEach(function (el) { el.setAttribute('hidden', ''); });
    return;
   }
   // width=880 = 2× the 438px render box — the full-size PNGs (1000-1500px)
   // made every breed change feel slow (QA 2026-08-10)
   var url = getBreedArtUrl(dog.breed, dog.sex, 880);
   var img = els[0].querySelector('img');
   if (img && img.dataset.src === url) {
    els.forEach(function (el) { el.removeAttribute('hidden'); });
    return;
   }
   // Same breed, other sex (male ↔ female art): the dog hops and the two
   // drawings cross-fade on the way up (they differ only by accessory). The
   // old img stays in the DOM (invisible) until the hop ends so the box keeps
   // its height and the transform origin doesn't jump mid-animation.
   var sexSwap = !!(img && img.classList.contains('is-loaded') && img.dataset.breed === dog.breed);

   // Instant feedback on a breed change: fade the previous art out right away
   // (QA 2026-08-10) — the emptied side reads as "something's coming" while
   // the new illustration preloads, instead of looking like nothing happened
   if (!sexSwap) {
    els.forEach(function (el) {
     var cur = el.querySelector('img');
     if (cur) cur.classList.remove('is-loaded');
    });
   }

   var preloader = new Image();
   var host = els[0];
   host._pendingSrc = url;
   preloader.onload = function () {
    if (host._pendingSrc !== url) return; // a newer breed won the race
    // Landscape art renders short at a fixed width — flag it so mobile CSS
    // can compensate (bigger + higher anchor); desktop's square box copes
    var isLandscape = preloader.naturalWidth > preloader.naturalHeight * 1.05;
    var imgHtml = '<img src="' + url + '" data-src="' + url + '" data-breed="' + dog.breed.replace(/"/g, '&quot;') + '" alt="">';
    if (sexSwap) {
     // Stack the new art (hidden) over the old, make sure it is decoded so
     // its first painted frame is never blank, then run the hop sequence.
     var pairs = els.map(function (el) {
      el.insertAdjacentHTML('beforeend', imgHtml);
      var stack = el.querySelectorAll('img');
      var topImg = stack[stack.length - 1];
      topImg.classList.add('quiz2-dog-side-swap');
      return { el: el, oldImg: stack[0], topImg: topImg };
     });
     // decode() never settles while the tab is hidden (and can reject), so
     // race it against a short timeout — the bytes are already preloaded.
     var decoded = new Promise(function (resolve) {
      setTimeout(resolve, 250);
      if (pairs[0].topImg.decode) pairs[0].topImg.decode().then(resolve, resolve);
     });
     decoded.then(function () {
      if (host._pendingSrc !== url) return;
      pairs.forEach(function (pr) {
       pr.el.classList.remove('quiz2-dog-side--hop');
       void pr.el.offsetWidth; // restart the hop on back-to-back toggles
       pr.el.classList.add('quiz2-dog-side--hop');
      });
      // Cross-fade on the way up: old out + new in at the same time, so the
      // side is never empty (a sequential fade left a visible gap — QA
      // 2026-09-22). Both 0.25s, done before the landing.
      setTimeout(function () {
       if (host._pendingSrc !== url) return;
       pairs.forEach(function (pr) {
        pr.oldImg.classList.add('is-out');
        pr.topImg.classList.add('is-loaded');
       });
      }, 120);
      setTimeout(function () {
       if (host._pendingSrc !== url) return;
       pairs.forEach(function (pr) {
        if (pr.oldImg.parentNode === pr.el) pr.el.removeChild(pr.oldImg);
        pr.topImg.classList.remove('quiz2-dog-side-swap');
        pr.el.classList.remove('quiz2-dog-side--hop');
       });
      }, 650);
     });
     return;
    }
    els.forEach(function (el) {
     el.classList.toggle('quiz2-dog-side--landscape', isLandscape);
     el.innerHTML = imgHtml;
     el.removeAttribute('hidden');
     var newImg = el.querySelector('img');
     void newImg.offsetWidth;
     newImg.classList.add('is-loaded');
    });
   };
   preloader.onerror = function () {
    if (host._pendingSrc !== url) return;
    // Female art missing for this breed → fall back to the male drawing
    // rather than an empty side (the male set is the complete baseline)
    if (dog.sex === 'female' && !preloader._triedMale) {
     preloader._triedMale = true;
     url = getBreedArtUrl(dog.breed, '', 880);
     host._pendingSrc = url;
     preloader.src = url;
     return;
    }
    els.forEach(function (el) { el.setAttribute('hidden', ''); }); // no art for this breed
   };
   preloader.src = url;
  },

  renderZodiac: function () {
   var el = document.getElementById('quiz2-zodiac');
   if (!el) return;
   var dog = this.currentDog();
   var show = false;
   if (dog && dog.birthdayMonth && dog.birthdayDay && window.QuizCalculator) {
    var sign = window.QuizCalculator.calculateZodiacSign({
     birthdayMonth: dog.birthdayMonth,
     birthdayDay: parseInt(dog.birthdayDay, 10)
    });
    if (sign) {
     var data = this.zodiacSigns.find(function (s) { return s.title.toLowerCase() === sign.name.toLowerCase(); });
     if (data) {
      document.getElementById('quiz2-zodiac-sign').textContent = data.title;
      document.getElementById('quiz2-zodiac-traits').textContent = data.description;
      var icon = document.getElementById('quiz2-zodiac-icon');
      if (icon) {
       icon.src = (window.SHOPIFY_CDN_BASE_URL || '') + 'horoscope-' + sign.name.toLowerCase() + '.svg';
      }
      // Soft pop on first reveal AND whenever the sign changes (editing the
      // birthday can flip it live) — class removal + reflow restarts the CSS
      // animation
      if (this._lastZodiacSign !== data.title) {
       this._lastZodiacSign = data.title;
       el.classList.remove('quiz2-zodiac--in');
       void el.offsetWidth;
       el.classList.add('quiz2-zodiac--in');
      }
      show = true;
     }
    }
   }
   if (show) {
    el.removeAttribute('hidden');
   } else {
    el.setAttribute('hidden', '');
    this._lastZodiacSign = null; // next reveal animates again
   }
  },

  renderDogSwitcher: function () {
   var el = document.getElementById('quiz2-dog-switcher');
   if (!el) return;
   var self = this;
   var show = this.state.dogs.length > 1 && DOG_SCREENS.indexOf(this.state.screen) > -1;
   if (!show) { el.setAttribute('hidden', ''); el.innerHTML = ''; return; }
   el.removeAttribute('hidden');
   el.innerHTML = this.state.dogs.map(function (dog, i) {
    var cls = 'quiz2-dog-switch' + (i === self.state.dogIndex ? ' active' : '');
    return '<button type="button" class="' + cls + '" data-switch-dog="' + i + '">' +
     ((dog.name || 'Dog ' + (i + 1)).replace(/</g, '&lt;')) + '</button>';
   }).join('');
  },

  renderErrors: function () {
   var self = this;
   document.querySelectorAll('[data-errors-for]').forEach(function (el) {
    if (el.dataset.errorsFor === self.state.screen && self.errors.length) {
     el.classList.add('has-errors');
     el.innerHTML = '<p class="quiz-error-message">' + self.errors.join('<br>') + '</p>';
    } else {
     el.classList.remove('has-errors');
     el.innerHTML = '';
    }
   });

   // Gate: the email field mirrors the error state (red border + a11y flag);
   // clears automatically because typing empties this.errors → renderErrors
   var emailEl = document.getElementById('quiz2-email');
   if (emailEl) {
    var emailInvalid = this.state.screen === 'gate' && this.errors.length > 0;
    emailEl.classList.toggle('is-invalid', emailInvalid);
    if (emailInvalid) { emailEl.setAttribute('aria-invalid', 'true'); } else { emailEl.removeAttribute('aria-invalid'); }
   }
  },

  // Desktop only: autofocusing the first empty field speeds up keyboard use.
  // On touch it does the opposite — the keyboard pops up unasked, and a
  // programmatic focus() on a <select> opens iOS's NATIVE picker, bypassing
  // the custom panel entirely (that's the double/native picker on step 3).
  focusFirst: function () {
   if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
   var screenEl = document.querySelector('[data-quiz-screen="' + this.state.screen + '"]');
   if (!screenEl) return;
   var focusable = screenEl.querySelectorAll('input:not([type="hidden"]), select');
   for (var i = 0; i < focusable.length; i++) {
    if (focusable[i].offsetParent !== null && !focusable[i].value) { focusable[i].focus({ preventScroll: true }); return; }
   }
  },

  // ── Breed typeahead ─────────────────────────────────────────────────────
  initBreedTypeahead: function () {
   var input = document.getElementById('quiz2-breed');
   var list = document.getElementById('quiz2-breed-list');
   if (!input || !list) return;
   var self = this;
   var breeds = window.quizBreeds || [];
   var activeIdx = -1;
   var current = [];

   function close() { list.classList.remove('is-open'); list.innerHTML = ''; activeIdx = -1; }

   function renderList(filtered, q) {
    current = filtered;
    if (!filtered.length) { close(); return; }
    list.innerHTML = filtered.map(function (b, i) {
     var idx = b.toLowerCase().indexOf(q.toLowerCase());
     var label = idx > -1
      ? b.slice(0, idx) + '<span class="quiz2-ta-match">' + b.slice(idx, idx + q.length) + '</span>' + b.slice(idx + q.length)
      : b;
     return '<li role="option" data-breed="' + b.replace(/"/g, '&quot;') + '" data-index="' + i + '">' + label + '</li>';
    }).join('');
    list.classList.add('is-open');
    activeIdx = -1;
   }

   function select(breed) {
    input.value = breed;
    self.setDogField('breed', breed);
    self.trackFieldCompleted('breed', breed);
    self.renderProgress();
    self.renderDogNames();
    self.renderSideIllustration();
    self.sizeBreedInput();
    close();
   }

   input.addEventListener('input', function () {
    self.sizeBreedInput();
    // The breed input bypasses the generic field handler, so clear any live
    // error here too (same clear-on-keystroke behavior as the other fields)
    if (self.errors.length) { self.errors = []; self.renderErrors(); }
    var q = this.value.trim();
    // Free text is not a valid breed until picked from the list
    self.setDogField('breed', '');
    if (!q) { close(); return; }
    var ql = q.toLowerCase();
    var starts = [], contains = [];
    breeds.forEach(function (b) {
     var bl = b.toLowerCase();
     if (bl.indexOf(ql) === 0) starts.push(b);
     else if (bl.indexOf(ql) > 0) contains.push(b);
    });
    renderList(starts.concat(contains).slice(0, 8), q);
   });

   input.addEventListener('keydown', function (e) {
    if (!list.classList.contains('is-open')) return;
    var items = list.querySelectorAll('li');
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
     e.preventDefault();
     activeIdx = e.key === 'ArrowDown' ? Math.min(activeIdx + 1, items.length - 1) : Math.max(activeIdx - 1, 0);
     items.forEach(function (li, i) { li.classList.toggle('is-active', i === activeIdx); });
    } else if (e.key === 'Enter') {
     e.preventDefault();
     if (activeIdx > -1) select(items[activeIdx].dataset.breed);
     else if (items.length === 1) select(items[0].dataset.breed);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
     close();
    }
   });

   list.addEventListener('mousedown', function (e) {
    var li = e.target.closest('li');
    if (li) { e.preventDefault(); select(li.dataset.breed); }
   });

   input.addEventListener('blur', function () {
    setTimeout(function () {
     // Snap to an exact (case-insensitive) match if typed fully
     var exact = breeds.find(function (b) { return b.toLowerCase() === input.value.trim().toLowerCase(); });
     if (exact) { select(exact); return; }
     close();
     // Typed text that matches no breed: say so (QA 2026-08-10) — the Continue
     // hides while the step is invalid, so this was a silent dead end
     var dog = self.currentDog();
     if (input.value.trim() && (!dog || !dog.breed) && window.QuizValidator) {
      self.errors = window.QuizValidator
       .getScreenErrors('basics', dog, self.state)
       .filter(function (m) { return /breed/i.test(m); });
      self.renderErrors();
     }
    }, 150);
   });
  }
 };

 if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { KismetQuiz.init(); });
 } else {
  KismetQuiz.init();
 }

 window.KismetQuiz = KismetQuiz;
})();
